import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { customerApi, scheduleApi, contractApi, paymentApi, productApi, workOrderApi, shootingRecordApi, smsApi, smsTemplateApi, notificationLogApi, orderConfirmationApi } from '../api';
import {
  CUSTOMER_STATUSES, CUSTOMER_STATUS_COLORS, GENDERS,
  SCHEDULE_CATEGORIES, SCHEDULE_CATEGORY_COLORS, SCHEDULE_STATUS_COLORS,
  SCHEDULE_SECTIONS, SCHEDULE_STATUSES,
  PAYMENT_TYPE_COLORS, PAYMENT_STATUS_COLORS, PAYMENT_TYPES, PAYMENT_STATUSES, PAYMENT_METHODS,
  CONTRACT_TYPES, CONTRACT_STATUSES, ITEM_CATEGORIES,
  INVENTORY_STATUSES, INVENTORY_STATUS_COLORS,
  PRODUCTION_PIPELINE, DATA_PIPELINE,
  REFERRAL_SOURCES, REFERRAL_SOURCE_DETAILS,
  CUSTOMER_TYPES,
} from '../constants';

// 가로 스크롤 컨테이너에 마우스 드래그 슬라이드를 붙이는 훅.
// 드래그 도중의 click 이벤트는 capture 단계에서 차단해 탭 버튼이 잘못 눌리지 않게 한다.
function useDragScroll<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const state = useRef({ isDown: false, startX: 0, scrollLeft: 0, moved: false });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const s = state.current;
    const onDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      s.isDown = true;
      s.moved = false;
      s.startX = e.pageX;
      s.scrollLeft = el.scrollLeft;
      el.style.cursor = 'grabbing';
    };
    const onUp = () => {
      if (!s.isDown) return;
      s.isDown = false;
      el.style.cursor = '';
    };
    const onMove = (e: MouseEvent) => {
      if (!s.isDown) return;
      const dx = e.pageX - s.startX;
      if (Math.abs(dx) > 3) s.moved = true;
      el.scrollLeft = s.scrollLeft - dx;
    };
    const onClickCapture = (e: Event) => {
      if (s.moved) {
        e.stopPropagation();
        e.preventDefault();
        s.moved = false;
      }
    };
    el.addEventListener('mousedown', onDown);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    el.addEventListener('click', onClickCapture, true);
    return () => {
      el.removeEventListener('mousedown', onDown);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      el.removeEventListener('click', onClickCapture, true);
    };
  }, []);
  return ref;
}

type TabType = 'OVERVIEW' | 'CONTRACTS' | 'SCHEDULES' | 'PAYMENTS' | 'SMS';
type OverviewSubTab = 'basic' | 'photographers' | 'partner' | 'select';

const TABS: { id: TabType; label: string; icon: string }[] = [
  { id: 'OVERVIEW', label: '기본정보', icon: 'ri-user-line' },
  { id: 'CONTRACTS', label: '계약정보', icon: 'ri-file-text-line' },
  { id: 'SCHEDULES', label: '일정정보', icon: 'ri-calendar-line' },
  { id: 'PAYMENTS', label: '결제정보', icon: 'ri-money-dollar-circle-line' },
  { id: 'SMS', label: '문자전송', icon: 'ri-message-2-line' },
];

const OVERVIEW_SUB_TABS: { id: OverviewSubTab; label: string; icon: string }[] = [
  { id: 'basic', label: '기본', icon: 'ri-user-line' },
  { id: 'photographers', label: '촬영자', icon: 'ri-camera-line' },
  { id: 'partner', label: '업체정보', icon: 'ri-store-line' },
  { id: 'select', label: '셀렉정보', icon: 'ri-image-line' },
];

const SHOOTING_TYPES = [
  { value: 'REHEARSAL', label: '리허설' },
  { value: 'MAIN', label: '본식' },
];

const SHOOTING_TYPE_COLORS: Record<string, string> = {
  REHEARSAL: 'bg-blue-100 text-blue-700',
  MAIN: 'bg-orange-100 text-orange-700',
};

const SHOOTING_INITIAL_FORM = {
  shootingType: 'REHEARSAL',
  shootingDt: '',
  photographer: '',
  totalShots: 0,
  selectShots: 0,
  retouchShots: 0,
  serviceShots: 0,
  originalPath: '',
  deliveryDt: '',
};

// SMS 템플릿 변수 정의 — 키/라벨/고객데이터 추출 규칙 (백엔드 replaceVariables와 동일해야 함)
function formatKoreanDate(v?: string | Date | null): string {
  if (!v) return '';
  try {
    return new Date(v).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return '';
  }
}

const SMS_TEMPLATE_VARS: Array<{
  key: string;
  label: string;
  resolve: (c: any) => string;
}> = [
  { key: '#{신랑이름}', label: '신랑이름', resolve: (c) => c?.groomName || '' },
  { key: '#{신부이름}', label: '신부이름', resolve: (c) => c?.brideName || '' },
  { key: '#{촬영일}', label: '촬영일', resolve: (c) => formatKoreanDate(c?.mainShootingDt || c?.rehearsalDt) },
  { key: '#{리허설일}', label: '리허설일', resolve: (c) => formatKoreanDate(c?.rehearsalDt) },
  { key: '#{셀렉일}', label: '셀렉일', resolve: (c) => formatKoreanDate(c?.selectDt) },
  { key: '#{예식일}', label: '예식일', resolve: (c) => formatKoreanDate(c?.weddingDt) },
];

// SMS 바이트 수 계산 — EUC-KR 기준 근사 (non-ASCII 2바이트, ASCII 1바이트).
// 실제 통신사 기준(SMS 90바이트 / LMS 2000바이트)과 일치시키기 위함.
function countSmsBytes(s: string): number {
  let bytes = 0;
  for (let i = 0; i < s.length; i++) {
    bytes += s.charCodeAt(i) < 0x80 ? 1 : 2;
  }
  return bytes;
}

// 템플릿 내용을 고객 데이터로 치환한 미리보기 JSX.
// 빈 변수는 빨간 placeholder, 치환된 값은 파란색 강조로 표시해 발송 전에 빠진 필드를 눈으로 확인 가능.
function renderSmsPreview(content: string, customer: any): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let remaining = content || '';
  let key = 0;
  while (remaining.length > 0) {
    let matched: { variable: typeof SMS_TEMPLATE_VARS[number]; index: number } | null = null;
    for (const v of SMS_TEMPLATE_VARS) {
      const i = remaining.indexOf(v.key);
      if (i === -1) continue;
      if (!matched || i < matched.index) matched = { variable: v, index: i };
    }
    if (!matched) {
      nodes.push(<span key={key++}>{remaining}</span>);
      break;
    }
    if (matched.index > 0) {
      nodes.push(<span key={key++}>{remaining.slice(0, matched.index)}</span>);
    }
    const resolved = matched.variable.resolve(customer);
    if (resolved) {
      nodes.push(<span key={key++} className="text-blue-600 font-medium">{resolved}</span>);
    } else {
      nodes.push(
        <span key={key++} className="text-red-600 bg-red-50 px-1 rounded text-xs font-medium">
          [{matched.variable.label}: 미입력]
        </span>,
      );
    }
    remaining = remaining.slice(matched.index + matched.variable.key.length);
  }
  return nodes;
}

export default function PhotoCustomerDetail() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const customerId = Number(searchParams.get('id'));
  const initialTab = (searchParams.get('tab') as TabType) || 'OVERVIEW';
  const initialSubTab = (searchParams.get('sub') as OverviewSubTab) || 'basic';

  const [activeTab, setActiveTab] = useState<TabType>(initialTab);
  const [activeSubTab, setActiveSubTab] = useState<OverviewSubTab>(initialSubTab);

  const changeTab = (tab: TabType) => {
    setActiveTab(tab);
    const params: Record<string, string> = { id: String(customerId), tab };
    if (tab === 'OVERVIEW') params.sub = activeSubTab;
    setSearchParams(params);
  };

  const changeSubTab = (sub: OverviewSubTab) => {
    setActiveSubTab(sub);
    setSearchParams({ id: String(customerId), tab: activeTab, sub });
  };

  const mainTabRef = useDragScroll<HTMLDivElement>();
  const subTabRef = useDragScroll<HTMLDivElement>();

  const [customer, setCustomer] = useState<any>(null);
  const [tabData, setTabData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<any>({});

  // 좌우 방향키로 메인 탭 전환 (입력 필드 포커스 중에는 비활성)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        if (target.isContentEditable) return;
      }
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const dir = e.key === 'ArrowLeft' ? -1 : 1;
      const idx = TABS.findIndex(t => t.id === activeTab);
      const next = idx + dir;
      if (next < 0 || next >= TABS.length) return;
      e.preventDefault();
      const nextTab = TABS[next].id;
      setActiveTab(nextTab);
      const params: Record<string, string> = { id: String(customerId), tab: nextTab };
      if (nextTab === 'OVERVIEW') params.sub = activeSubTab;
      setSearchParams(params);
      setTabData(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeTab, activeSubTab, customerId, setSearchParams]);

  // 촬영기록
  const [staffList, setStaffList] = useState<any[]>([]);
  const [shootingRecords, setShootingRecords] = useState<any[]>([]);
  const [showShootingModal, setShowShootingModal] = useState(false);
  const [editingShooting, setEditingShooting] = useState<any>(null);
  const [shootingForm, setShootingForm] = useState<any>(SHOOTING_INITIAL_FORM);

  // 문자전송
  const [smsLogs, setSmsLogs] = useState<any[]>([]);
  const [smsTemplates, setSmsTemplates] = useState<any[]>([]);
  const [showSmsModal, setShowSmsModal] = useState(false);
  // 빠른 문자 전송 모달의 수신자 목록 (1명 이상). 2명 이상이면 /sms/bulk 로, 1명은 /sms/send 로 자동 분기.
  const [smsRecipients, setSmsRecipients] = useState<Array<{ to: string; name?: string; label?: string }>>([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [smsForm, setSmsForm] = useState({ to: '', title: '', text: '', notificationType: 'GENERAL' });
  const smsTextRef = useRef<HTMLTextAreaElement | null>(null);
  const insertSmsTextVar = (varKey: string) => {
    const el = smsTextRef.current;
    const current = smsForm.text || '';
    if (!el) {
      setSmsForm({ ...smsForm, text: current + varKey });
      return;
    }
    const start = el.selectionStart ?? current.length;
    const end = el.selectionEnd ?? current.length;
    const next = current.slice(0, start) + varKey + current.slice(end);
    setSmsForm({ ...smsForm, text: next });
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + varKey.length;
      el.setSelectionRange(pos, pos);
    });
  };
  const [templateForm, setTemplateForm] = useState({ templateName: '', title: '', content: '', templateType: 'GENERAL' });
  const templateContentRef = useRef<HTMLTextAreaElement | null>(null);

  // 커서 위치에 변수 삽입. textarea ref가 없으면 끝에 붙임.
  const insertTemplateVar = (varKey: string) => {
    const el = templateContentRef.current;
    const current = templateForm.content || '';
    if (!el) {
      setTemplateForm({ ...templateForm, content: current + varKey });
      return;
    }
    const start = el.selectionStart ?? current.length;
    const end = el.selectionEnd ?? current.length;
    const next = current.slice(0, start) + varKey + current.slice(end);
    setTemplateForm({ ...templateForm, content: next });
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + varKey.length;
      el.setSelectionRange(pos, pos);
    });
  };
  const [editingTemplate, setEditingTemplate] = useState<any>(null);
  const [smsTab, setSmsTab] = useState<'send' | 'history' | 'templates'>('history');

  // 발주확인(역발주)
  const [showOrderConfirmModal, setShowOrderConfirmModal] = useState(false);
  const [orderConfirmForm, setOrderConfirmForm] = useState({ recipientEmail: '', recipientName: '', commissionInfo: '' });
  const [orderConfirmPdfUrl, setOrderConfirmPdfUrl] = useState<string>('');

  const fetchOrderConfirmPdf = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(orderConfirmationApi.previewUrl(customerId), {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const blob = await res.blob();
      setOrderConfirmPdfUrl(URL.createObjectURL(blob));
    } catch { setOrderConfirmPdfUrl(''); }
  };

  // 일정 모달 (여러 개 동시 등록)
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleForm, setScheduleForm] = useState<any>({});
  const [scheduleItems, setScheduleItems] = useState<any[]>([]);

  // 계약 등록 모달
  const [showContractModal, setShowContractModal] = useState(false);
  const [contractForm, setContractForm] = useState<any>({});
  const [contractItems, setContractItems] = useState<any[]>([]);
  // 계약 품목 (모든 계약 자동 로드)
  const [contractItemsMap, setContractItemsMap] = useState<Record<number, any[]>>({});
  // 고객 검색 (계약 모달용)
  const [contractCustomerQuery, setContractCustomerQuery] = useState('');
  const [contractCustomerStatusFilter, setContractCustomerStatusFilter] = useState('');
  const [contractCustomerResults, setContractCustomerResults] = useState<any[]>([]);
  const [contractSelectedCustomer, setContractSelectedCustomer] = useState<any>(null);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  // 상품 검색 (계약 품목용)
  const [products, setProducts] = useState<any[]>([]);
  const [showProductModal, setShowProductModal] = useState(false);
  const [productQuery, setProductQuery] = useState('');
  const [addingItemIndex, setAddingItemIndex] = useState<number | null>(null);

  // 결제 등록 모달
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState<any>({});
  const [selectedPayment, setSelectedPayment] = useState<any>(null);

  const fetchData = useCallback(async () => {
    if (!customerId) return;
    setIsLoading(true);
    try {
      const data = await customerApi.detail(customerId, activeTab);
      setCustomer(data.customer);
      setTabData(data);
      if (activeTab === 'OVERVIEW') {
        setEditForm(data.customer || {});
      }
      // 결제정보 탭에서 계약 데이터도 함께 로드 (요약 표시용)
      if (activeTab === 'PAYMENTS') {
        customerApi.detail(customerId, 'CONTRACTS').then((cData: any) => {
          setTabData((prev: any) => ({ ...prev, contracts: cData.contracts }));
        }).catch(() => {});
      }
    } catch (err) {
      console.error('고객 상세 조회 실패:', err);
    } finally {
      setIsLoading(false);
    }
  }, [customerId, activeTab]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // 워킹고객으로 변경되면 업체정보 서브탭에서 자동으로 기본 서브탭으로 복귀
  useEffect(() => {
    const channel = isEditing ? editForm.customerChannel : customer?.customerChannel;
    if (activeSubTab === 'partner' && channel && channel !== 'VENDOR') {
      setActiveSubTab('basic');
    }
  }, [customer?.customerChannel, editForm.customerChannel, isEditing, activeSubTab]);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    fetch('https://api.wedmin.biz/user?status=ACTIVE&size=100', {
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    })
      .then(res => res.json())
      .then(json => setStaffList(json.data?.items || json.items || []))
      .catch(() => {});
  }, []);

  const handleUpdate = async () => {
    try {
      const {
        photoCustomerIdx, ownerVendor, assignedStaff,
        rehearsalPhotographer, rehearsalPhotographer2,
        mainPhotographer, mainPhotographer2,
        selectManager,
        partnerVendor, deleteYn, createDt, updateDt, createdDt, updatedDt,
        partnerVendorNames, contractDt,
        totalContractAmount, totalPaidAmount, remainingAmount, purchaseItems,
        ...rest
      } = editForm;
      const customerData: any = { ...rest };
      // 빈 문자열 FK → 제거 (서버 @IsNumber 실패 방지)
      const fkFields = [
        'rehearsalPhotographerIdx', 'rehearsalPhotographer2Idx',
        'mainPhotographerIdx', 'mainPhotographer2Idx',
        'selectManagerIdx', 'assignedStaffIdx', 'partnerVendorIdx',
      ];
      for (const f of fkFields) if (customerData[f] === '' || customerData[f] === null) delete customerData[f];
      // 빈 enum → 제거
      const enumFields = ['customerType', 'customerStatus', 'customerChannel', 'shipMethod', 'courierPayment', 'selectMethod', 'selectContent'];
      for (const f of enumFields) if (customerData[f] === '') delete customerData[f];
      // 빈 이메일 → 제거
      if (!customerData.groomEmail) delete customerData.groomEmail;
      if (!customerData.brideEmail) delete customerData.brideEmail;
      if (!customerData.email) delete customerData.email;

      await customerApi.update(customerId, customerData);

      alert('수정되었습니다.');
      setIsEditing(false);
      fetchData();
    } catch (err: any) {
      alert(`수정 실패: ${err.message}`);
    }
  };

  const handleDelete = async () => {
    if (!confirm('정말 이 고객을 삭제하시겠습니까?')) return;
    try {
      await customerApi.delete(customerId);
      alert('삭제되었습니다.');
      navigate('/photo-studio/customers');
    } catch (err: any) {
      alert(`삭제 실패: ${err.message}`);
    }
  };

  // --- 촬영기록 핸들러 ---
  const fetchShootingRecords = useCallback(async () => {
    if (!customerId) return;
    try {
      const data = await shootingRecordApi.list({ customerIdx: customerId, size: 100 });
      setShootingRecords(data.items || []);
    } catch (err) {
      console.error('촬영기록 조회 실패:', err);
    }
  }, [customerId]);

  // shooting records are fetched on-demand if needed elsewhere
  // useEffect for SHOOTING tab removed (tab replaced by SMS)

  const openShootingModal = (item?: any) => {
    if (item) {
      setEditingShooting(item);
      setShootingForm({
        shootingType: item.shootingType || 'REHEARSAL',
        shootingDt: item.shootingDt?.slice(0, 10) || '',
        photographer: item.photographer || '',
        totalShots: item.totalShots || 0,
        selectShots: item.selectShots || 0,
        retouchShots: item.retouchShots || 0,
        serviceShots: item.serviceShots || 0,
        originalPath: item.originalPath || '',
        deliveryDt: item.deliveryDt?.slice(0, 10) || '',
      });
    } else {
      setEditingShooting(null);
      setShootingForm(SHOOTING_INITIAL_FORM);
    }
    setShowShootingModal(true);
  };

  const handleSaveShooting = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!shootingForm.shootingDt) { alert('촬영일은 필수입니다.'); return; }
    try {
      const payload = { ...shootingForm, customerIdx: customerId };
      if (editingShooting) {
        await shootingRecordApi.update(editingShooting.shootingRecordIdx, payload);
        alert('수정되었습니다.');
      } else {
        await shootingRecordApi.create(payload);
        alert('등록되었습니다.');
      }
      setShowShootingModal(false);
      fetchShootingRecords();
    } catch (err: any) {
      alert(`저장 실패: ${err.message}`);
    }
  };

  const handleDeleteShooting = async (item: any) => {
    if (!confirm('이 촬영 기록을 삭제하시겠습니까?')) return;
    try {
      await shootingRecordApi.delete(item.shootingRecordIdx);
      alert('삭제되었습니다.');
      fetchShootingRecords();
    } catch (err: any) {
      alert(`삭제 실패: ${err.message}`);
    }
  };

  const formatShootingDate = (dt?: string) => {
    if (!dt) return '-';
    return new Date(dt).toLocaleDateString('ko-KR', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // --- 문자전송 핸들러 ---
  const fetchSmsLogs = useCallback(async () => {
    if (!customerId) return;
    try {
      const data = await notificationLogApi.list({ photoCustomerIdx: customerId, size: 100 });
      setSmsLogs(data.items || []);
    } catch (err) {
      console.error('문자 내역 조회 실패:', err);
    }
  }, [customerId]);

  const fetchSmsTemplates = useCallback(async () => {
    try {
      const data = await smsTemplateApi.list({ size: 100 });
      setSmsTemplates(data.items || []);
    } catch (err) {
      console.error('템플릿 조회 실패:', err);
    }
  }, []);

  // 템플릿은 어느 화면(촬영자 서브탭 등)에서 문자 전송 모달을 열어도 즉시 선택 가능해야 하므로
  // 컴포넌트 마운트 시 한 번 로드해둔다. 발송 내역 로그는 SMS 탭에 들어왔을 때만 로드.
  useEffect(() => { fetchSmsTemplates(); }, [fetchSmsTemplates]);
  useEffect(() => {
    if (activeTab === 'SMS') {
      fetchSmsLogs();
    }
  }, [activeTab, fetchSmsLogs]);

  // 특정 사용자(촬영자/담당자 등)에게 바로 문자 보내기: 인라인 모달을 열어 같은 화면에서 발송.
  // 모달 내부에서 다른 수신자를 추가로 더할 수 있음 (다중 발송).
  const sendSmsToUser = (user: any, label?: string) => {
    if (!user?.phoneNumber) return;
    setSmsRecipients([{ to: user.phoneNumber, name: user.name, label: label || user.name || '' }]);
    setSmsForm({ to: user.phoneNumber, title: '', text: '', notificationType: 'GENERAL' });
    setShowSmsModal(true);
  };

  const closeSmsModal = () => {
    setShowSmsModal(false);
    setSmsRecipients([]);
  };

  const handleSendSms = async (e: React.FormEvent) => {
    e.preventDefault();

    // 모달 (빠른 문자 전송) 경로 — smsRecipients 배열 기준으로 bulk 발송.
    // 1명이든 N명이든 bulk 엔드포인트가 모두 처리하므로 항상 /sms/bulk 사용.
    if (showSmsModal) {
      if (smsRecipients.length === 0) { alert('수신자를 1명 이상 추가해주세요.'); return; }
      if (!smsForm.text) { alert('내용을 입력해주세요.'); return; }
      try {
        const result = await smsApi.sendBulk({
          recipients: smsRecipients.map(r => ({
            to: r.to,
            name: r.name,
            photoCustomerIdx: Number(customerId),
          })),
          text: smsForm.text,
          notificationType: smsForm.notificationType,
        });
        console.log('[SMS bulk send] response:', result);
        const successCount = result?.successCount ?? 0;
        const failedCount = result?.failedCount ?? 0;
        if (failedCount > 0) {
          alert(`${successCount}명 발송 성공, ${failedCount}명 실패.`);
        } else {
          alert(`${successCount}명에게 발송 완료`);
        }
        setSmsForm({ to: '', title: '', text: '', notificationType: 'GENERAL' });
        setSmsRecipients([]);
        setShowSmsModal(false);
        fetchSmsLogs();
      } catch (err: any) {
        console.error('[SMS bulk send] error:', err);
        alert(`발송 실패: ${err.message}`);
      }
      return;
    }

    // 기존 단일 발송 경로 (문자전송 탭 > 문자 발송 폼)
    if (!smsForm.to || !smsForm.text) { alert('수신번호와 내용은 필수입니다.'); return; }
    try {
      const result = await smsApi.send({
        photoCustomerIdx: Number(customerId),
        to: smsForm.to,
        title: smsForm.title,
        text: smsForm.text,
        notificationType: smsForm.notificationType,
      });
      console.log('[SMS send] response:', result);
      if (result.success) {
        alert('문자가 발송되었습니다.');
        setSmsForm({ to: '', title: '', text: '', notificationType: 'GENERAL' });
        fetchSmsLogs();
      } else {
        alert(`발송 실패: ${result.error || '알 수 없는 오류'}`);
      }
    } catch (err: any) {
      console.error('[SMS send] error:', err);
      alert(`발송 실패: ${err.message}`);
    }
  };

  const handleSaveTemplate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!templateForm.templateName || !templateForm.content) { alert('템플릿명과 내용은 필수입니다.'); return; }
    try {
      if (editingTemplate) {
        await smsTemplateApi.update(editingTemplate.photoSmsTemplateIdx, templateForm);
        alert('수정되었습니다.');
      } else {
        await smsTemplateApi.create(templateForm);
        alert('등록되었습니다.');
      }
      setShowTemplateModal(false);
      setEditingTemplate(null);
      setTemplateForm({ templateName: '', title: '', content: '', templateType: 'GENERAL' });
      fetchSmsTemplates();
    } catch (err: any) {
      alert(`저장 실패: ${err.message}`);
    }
  };

  const handleDeleteTemplate = async (item: any) => {
    if (!confirm('이 템플릿을 삭제하시겠습니까?')) return;
    try {
      await smsTemplateApi.delete(item.photoSmsTemplateIdx);
      alert('삭제되었습니다.');
      fetchSmsTemplates();
    } catch (err: any) {
      alert(`삭제 실패: ${err.message}`);
    }
  };

  const applyTemplate = (template: any) => {
    let content = template.content;
    if (customer) {
      content = content
        .replace(/#{신랑이름}/g, customer.groomName || '')
        .replace(/#{신부이름}/g, customer.brideName || '')
        .replace(/#{촬영일}/g, customer.mainShootingDt ? new Date(customer.mainShootingDt).toLocaleDateString('ko-KR') : '')
        .replace(/#{리허설일}/g, customer.rehearsalDt ? new Date(customer.rehearsalDt).toLocaleDateString('ko-KR') : '')
        .replace(/#{셀렉일}/g, customer.selectDt ? new Date(customer.selectDt).toLocaleDateString('ko-KR') : '')
        .replace(/#{예식일}/g, customer.weddingDt ? new Date(customer.weddingDt).toLocaleDateString('ko-KR') : '');
    }
    setSmsForm(prev => ({ ...prev, text: content, title: template.title || '' }));
  };

  // --- 발주확인(역발주) 핸들러 ---
  const handleSendOrderConfirmation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orderConfirmForm.recipientEmail) { alert('수신 이메일은 필수입니다.'); return; }
    try {
      const result = await orderConfirmationApi.send({
        photoCustomerIdx: Number(customerId),
        recipientEmail: orderConfirmForm.recipientEmail,
        recipientName: orderConfirmForm.recipientName,
        commissionInfo: orderConfirmForm.commissionInfo,
      });
      if (result.success) {
        alert('발주확인(역발주)서가 발송되었습니다.');
        setShowOrderConfirmModal(false);
        setOrderConfirmForm({ recipientEmail: '', recipientName: '', commissionInfo: '' });
      } else {
        alert(`발송 실패: ${result.error || '알 수 없는 오류'}`);
      }
    } catch (err: any) {
      alert(`발송 실패: ${err.message}`);
    }
  };

  // --- 일정 핸들러 ---
  // 셀렉실 목록
  const [selectRooms, setSelectRooms] = useState<any[]>([]);

  const fetchSelectRooms = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch('https://api.wedmin.biz/room?type=SELECT&isActive=true', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await res.json();
      const rooms = Array.isArray(result.data) ? result.data : Array.isArray(result) ? result : [];
      setSelectRooms(rooms);
    } catch {}
  }, []);

  const SECTION_MAP: Record<string, string> = {
    TASK: 'TASK', CONSULTATION: 'CONSULTATION',
    REHEARSAL_SHOOTING: 'SHOOTING', MAIN_SHOOTING: 'SHOOTING',
    SELECT: 'SELECT', WEDDING: 'TASK', PAYMENT: 'TASK', DELIVERY: 'TASK',
  };

  const emptyScheduleItem = () => ({
    scheduleCategory: 'CONSULTATION',
    title: '',
    startDt: '',
    endDt: '',
    memo: '',
    roomIdx: '' as any,
  });

  const openScheduleModal = () => {
    setScheduleItems([emptyScheduleItem()]);
    fetchSelectRooms();
    setShowScheduleModal(true);
  };

  const handleScheduleItemChange = (index: number, field: string, value: string) => {
    setScheduleItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };

  const addScheduleItem = () => {
    setScheduleItems(prev => [...prev, emptyScheduleItem()]);
  };

  const removeScheduleItem = (index: number) => {
    if (scheduleItems.length === 1) return;
    setScheduleItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleCreateSchedule = async (e: React.FormEvent) => {
    e.preventDefault();
    const validItems = scheduleItems.filter(item => item.title && item.startDt);
    if (validItems.length === 0) {
      alert('제목과 시작일시는 필수입니다.');
      return;
    }
    try {
      await Promise.all(validItems.map(item =>
        scheduleApi.create({
          section: SECTION_MAP[item.scheduleCategory] || 'TASK',
          ...item,
          endDt: item.endDt || item.startDt,
          photoCustomerIdx: customerId,
          customerName: customer?.groomName || customer?.brideName || '',
          customerPhone: customer?.groomPhone || customer?.bridePhone || '',
          roomIdx: item.roomIdx ? parseInt(item.roomIdx) : undefined,
        })
      ));
      alert(`${validItems.length}건의 일정이 등록되었습니다.`);
      setShowScheduleModal(false);
      fetchData();
    } catch (err: any) {
      alert(`등록 실패: ${err.message}`);
    }
  };

  // --- 계약 품목 자동 로드 ---
  useEffect(() => {
    if (activeTab === 'CONTRACTS' && tabData?.contracts?.length > 0) {
      const loadAllItems = async () => {
        const map: Record<number, any[]> = {};
        await Promise.all((tabData.contracts || []).map(async (c: any) => {
          const idx = c.photoContractIdx || c.photo_contract_idx;
          if (!idx) return;
          try {
            const data = await contractApi.detail(idx);
            map[idx] = data.items || [];
          } catch { map[idx] = []; }
        }));
        setContractItemsMap(map);
      };
      loadAllItems();
    }
  }, [activeTab, tabData?.contracts]);

  const handleWorkStatusChange = async (item: any, newStage: string, contractIdx?: number) => {
    try {
      const itemIdx = item.photoContractItemIdx || item.photo_contract_item_idx;
      const contractId = contractIdx;
      // Find existing work order for this item
      const woRes = await workOrderApi.list({ photoContractItemIdx: itemIdx, size: 1 });
      const workOrders = woRes?.items || [];
      let workOrderIdx: number | null = null;

      if (Array.isArray(workOrders) && workOrders.length > 0) {
        workOrderIdx = workOrders[0].photoWorkOrderIdx;
      }

      if (!workOrderIdx) {
        // Create a new work order with required fields
        const created = await workOrderApi.create({
          photoContractItemIdx: itemIdx,
          photoContractIdx: contractId,
          workType: item.workType === 'DATA' ? 'REHEARSAL_AND_MAIN' : 'CUSTOM',
        });
        workOrderIdx = created?.photoWorkOrderIdx;
      }

      if (workOrderIdx) {
        await workOrderApi.changeStage(workOrderIdx, newStage);
      }

      // Refresh data
      fetchData();
    } catch (err: any) {
      console.error('작업 상태 변경 실패:', err);
      alert(`상태 변경 실패: ${err.message || '알 수 없는 오류'}`);
    }
  };

  // --- 계약 핸들러 ---
  const openContractModal = () => {
    // 현재 보고 있는 고객을 기본 선택
    if (customer) {
      setContractSelectedCustomer({ photoCustomerIdx: customerId, name: customer.groomName || customer.brideName, phone: customer.groomPhone || customer.bridePhone });
      setContractCustomerQuery(customer.groomName || customer.brideName || '');
    } else {
      setContractSelectedCustomer(null);
      setContractCustomerQuery('');
    }
    setContractForm({
      contractType: 'REHEARSAL_AND_MAIN',
      contractDt: new Date().toISOString().split('T')[0],
      orderChannel: 'DIRECT',
      partnerVendorName: '',
      memo: '',
    });
    setContractItems([]);
    setShowCustomerDropdown(false);
    setContractCustomerStatusFilter('');
    setShowContractModal(true);
  };

  // 고객 검색
  useEffect(() => {
    if (!showContractModal) {
      setContractCustomerResults([]);
      return;
    }
    // 검색어 또는 상태 필터가 있으면 검색
    if (!contractCustomerQuery && !contractCustomerStatusFilter) {
      setContractCustomerResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const data = await customerApi.list({
          q: contractCustomerQuery || undefined,
          status: contractCustomerStatusFilter || undefined,
          size: 20,
        });
        setContractCustomerResults(data.items || []);
        setShowCustomerDropdown(true);
      } catch {
        setContractCustomerResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [contractCustomerQuery, contractCustomerStatusFilter, showContractModal]);

  const selectContractCustomer = (c: any) => {
    setContractSelectedCustomer(c);
    setContractCustomerQuery(c.name);
    setShowCustomerDropdown(false);
  };

  // 상품 검색
  const openProductSelector = (itemIndex: number) => {
    setAddingItemIndex(itemIndex);
    setProductQuery('');
    setProducts([]);
    setShowProductModal(true);
    // 초기 로드
    productApi.list({ size: 50 }).then((data: any) => setProducts(data.items || [])).catch(() => {});
  };

  useEffect(() => {
    if (!showProductModal) return;
    const timer = setTimeout(async () => {
      try {
        const data = await productApi.list({ q: productQuery || undefined, size: 50 });
        setProducts(data.items || []);
      } catch {
        setProducts([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [productQuery, showProductModal]);

  const selectProduct = (product: any) => {
    if (addingItemIndex !== null && addingItemIndex >= 0) {
      // 기존 품목 업데이트
      setContractItems(contractItems.map((item, idx) =>
        idx === addingItemIndex ? {
          ...item,
          productIdx: product.productIdx,
          productName: product.name,
          itemCategory: product.category || item.itemCategory,
          unitPrice: product.salesPrice || 0,
          costPrice: product.costPrice || 0,
        } : item
      ));
    } else {
      // 새 품목 추가
      setContractItems([...contractItems, {
        id: Date.now(),
        productIdx: product.productIdx,
        itemCategory: product.category || 'ALBUM',
        productName: product.name,
        quantity: 1,
        unitPrice: product.salesPrice || 0,
        costPrice: product.costPrice || 0,
        memo: '',
      }]);
    }
    setShowProductModal(false);
    setAddingItemIndex(null);
  };

  const addContractItemManual = () => {
    setContractItems([...contractItems, {
      id: Date.now(),
      itemCategory: 'ALBUM',
      productName: '',
      quantity: 1,
      unitPrice: 0,
      memo: '',
    }]);
  };

  const removeContractItem = (id: number) => {
    setContractItems(contractItems.filter(item => item.id !== id));
  };

  const updateContractItem = (id: number, field: string, value: any) => {
    setContractItems(contractItems.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const handleCreateContract = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contractSelectedCustomer) {
      alert('고객을 선택해주세요.');
      return;
    }
    if (contractItems.length === 0) {
      alert('품목을 하나 이상 추가해주세요.');
      return;
    }
    if (contractItems.some(item => !item.productName || !item.unitPrice)) {
      alert('모든 품목의 상품명과 단가는 필수입니다.');
      return;
    }
    try {
      const totalAmount = contractItems.reduce((sum: number, item: any) => sum + (item.unitPrice * item.quantity), 0);
      const { orderChannel, ...contractData } = contractForm;
      await contractApi.create({
        ...contractData,
        photoCustomerIdx: contractSelectedCustomer.photoCustomerIdx,
        totalAmount,
        items: contractItems.map(({ id, size, color, ...item }) => item),
      });

      // 계약일/대여일을 일정에 자동 등록
      const custName = contractSelectedCustomer.name || '';
      const schedulePromises: Promise<any>[] = [];
      if (contractForm.contractDt) {
        schedulePromises.push(scheduleApi.create({
          section: 'CONSULTATION', scheduleCategory: 'CONSULTATION',
          title: `${custName} 계약`,
          startDt: contractForm.contractDt,
          photoCustomerIdx: contractSelectedCustomer.photoCustomerIdx,
          customerName: custName,
        }).catch(() => {}));
      }
      await Promise.all(schedulePromises);

      alert('계약이 등록되었습니다.');
      setShowContractModal(false);
      fetchData();
    } catch (err: any) {
      alert(`등록 실패: ${err.message}`);
    }
  };

  // --- 결제 핸들러 ---
  const openPaymentModal = () => {
    setPaymentForm({
      photoCustomerIdx: customerId,
      photoContractIdx: '',
      items: [{ paidDt: new Date().toISOString().split('T')[0], amount: 0, paymentType: 'CONTRACT', status: 'COMPLETED', paymentMethod: 'CARD', memo: '' }],
    });
    // 계약 목록 + 품목 로드
    customerApi.detail(customerId, 'CONTRACTS').then(async (data: any) => {
      const contracts = data.contracts || [];
      setTabData((prev: any) => ({ ...prev, contracts }));
      // 각 계약의 품목도 로드
      const map: Record<number, any[]> = {};
      await Promise.all(contracts.map(async (c: any) => {
        const idx = c.photoContractIdx || c.photo_contract_idx;
        if (!idx) return;
        try {
          const detail = await contractApi.detail(idx);
          map[idx] = detail.items || [];
        } catch { map[idx] = []; }
      }));
      setContractItemsMap(prev => ({ ...prev, ...map }));
    }).catch(() => {});
    setShowPaymentModal(true);
  };

  const handleCreatePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!paymentForm.photoContractIdx) {
      alert('계약을 선택해주세요.');
      return;
    }
    const items = paymentForm.items || [paymentForm];
    const validItems = items.filter((i: any) => i.amount > 0 && i.paidDt);
    if (validItems.length === 0) {
      alert('결제 금액과 결제일을 입력해주세요.');
      return;
    }
    try {
      await Promise.all(validItems.map((item: any) =>
        paymentApi.create({
          photoCustomerIdx: customerId,
          photoContractIdx: Number(paymentForm.photoContractIdx),
          ...item,
          amount: Number(item.amount),
        })
      ));
      // 예정 상태인 결제는 일정에 자동 등록
      const scheduledItems = validItems.filter((i: any) => i.status === 'SCHEDULED');
      if (scheduledItems.length > 0 && customer) {
        const getTypeLabel = (t: string) => ({ FULL: '일시납부', CONTRACT: '계약금', INTERMEDIATE: '중도금', BALANCE: '잔금', ETC: '결제' }[t] || '결제');
        await Promise.all(scheduledItems.map((i: any) =>
          scheduleApi.create({
            section: 'TASK',
            scheduleCategory: 'PAYMENT',
            title: `${(customer.groomName || customer.brideName || '')} ${getTypeLabel(i.paymentType)} ${Number(i.amount).toLocaleString()}원`,
            startDt: i.paidDt,
            photoCustomerIdx: customerId,
            customerName: (customer.groomName || customer.brideName || ''),
          }).catch(() => {})
        ));
      }

      alert(`${validItems.length}건의 결제가 등록되었습니다.`);
      setShowPaymentModal(false);
      fetchData();
    } catch (err: any) {
      alert(`등록 실패: ${err.message}`);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 p-8">
        {/* 헤더 */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/photo-studio/customers')}
              className="text-gray-600 hover:text-gray-800 w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded transition-colors cursor-pointer">
              <i className="ri-arrow-left-line text-xl"></i>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">고객 상세정보</h1>
              <p className="text-sm text-gray-600 mt-1">스튜디오 고객 상세 정보</p>
            </div>
          </div>
          {activeTab === 'OVERVIEW' && (
            <div className="flex items-center gap-3">
              {customer && (
                <select value={customer.customerStatus || ''} onChange={async (e) => {
                  try {
                    await customerApi.update(customerId, { customerStatus: e.target.value });
                    fetchData();
                  } catch (err: any) { alert(`상태 변경 실패: ${err.message}`); }
                }} className={`px-4 py-2 rounded-full text-sm font-semibold cursor-pointer border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none pr-8 ${CUSTOMER_STATUS_COLORS[customer.customerStatus] || 'bg-gray-100 text-gray-700'}`}
                  style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center' }}>
                  {CUSTOMER_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              )}
              {isEditing ? (
                <>
                  <button onClick={() => setIsEditing(false)}
                    className="px-6 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 font-medium cursor-pointer">취소</button>
                  <button onClick={handleUpdate}
                    className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium cursor-pointer">저장</button>
                </>
              ) : (
                <>
                  {customer?.referralSource === '웨딩플래너' && (
                    <button onClick={() => { setOrderConfirmForm({ recipientEmail: '', recipientName: customer?.referralSourceDetail || '', commissionInfo: '' }); setOrderConfirmPdfUrl(''); setShowOrderConfirmModal(true); fetchOrderConfirmPdf(); }}
                      className="bg-white text-blue-600 border border-blue-300 px-5 py-2.5 rounded-lg hover:bg-blue-50 flex items-center gap-2 cursor-pointer text-sm">
                      <i className="ri-mail-send-line"></i> 발주확인서(역발주) 발송
                    </button>
                  )}
                  <button onClick={() => setIsEditing(true)}
                    className="bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 flex items-center gap-2 cursor-pointer">
                    <i className="ri-edit-line"></i> 수정
                  </button>
                  <button onClick={handleDelete}
                    className="bg-red-600 text-white px-6 py-2.5 rounded-lg hover:bg-red-700 flex items-center gap-2 cursor-pointer">
                    <i className="ri-delete-bin-line"></i> 삭제
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* 고객 요약 카드 */}
        {customer && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex items-start gap-6">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-50 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-user-3-line text-2xl text-blue-600"></i>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-3">
                  <h2 className="text-xl font-bold text-gray-800">
                    {[customer.groomName, customer.brideName].filter(Boolean).join(' / ') || '-'}
                  </h2>
                  <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${CUSTOMER_STATUS_COLORS[customer.customerStatus] || 'bg-gray-100 text-gray-700'}`}>
                    {customer.customerStatus}
                  </span>
                </div>
                <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-x-6 gap-y-2 text-sm text-gray-600">
                  {customer.groomPhone && <span><i className="ri-phone-line mr-1 text-gray-400"></i>신랑 {customer.groomPhone}</span>}
                  {customer.bridePhone && <span><i className="ri-phone-line mr-1 text-gray-400"></i>신부 {customer.bridePhone}</span>}
                  {(customer.groomEmail || customer.brideEmail || customer.email) && (
                    <span className="truncate"><i className="ri-mail-line mr-1 text-gray-400"></i>{customer.groomEmail || customer.brideEmail || customer.email}</span>
                  )}
                  {customer.weddingDt && <span><i className="ri-calendar-line mr-1 text-gray-400"></i>예식 {new Date(customer.weddingDt).toLocaleDateString('ko-KR')}</span>}
                  {customer.rehearsalDt && <span><i className="ri-camera-line mr-1 text-gray-400"></i>리허설 {new Date(customer.rehearsalDt).toLocaleDateString('ko-KR')}</span>}
                  {customer.mainShootingDt && <span><i className="ri-camera-line mr-1 text-gray-400"></i>본식 {new Date(customer.mainShootingDt).toLocaleDateString('ko-KR')}</span>}
                  {customer.selectDt && <span><i className="ri-image-line mr-1 text-gray-400"></i>셀렉 {new Date(customer.selectDt).toLocaleDateString('ko-KR')}</span>}
                  {customer.assignedStaff && <span><i className="ri-user-line mr-1 text-gray-400"></i>담당 {customer.assignedStaff.name}</span>}
                  {customer.mainPhotographer && (
                    <span><i className="ri-shutter-line mr-1 text-gray-400"></i>본식촬영 {customer.mainPhotographer.name}{customer.mainPhotographer2 ? ` / ${customer.mainPhotographer2.name}` : ''}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 탭 메뉴 */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div ref={mainTabRef} className="flex border-b overflow-x-auto scroll-smooth touch-pan-x overscroll-x-contain cursor-grab select-none [&::-webkit-scrollbar]:hidden [scrollbar-width:none] [-ms-overflow-style:none]">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => { changeTab(tab.id); setTabData(null); }}
                className={`flex-1 min-w-[7rem] px-4 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.id ? 'border-blue-500 text-blue-600 bg-blue-50' : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className={`${tab.icon} mr-2`}></i>{tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* 탭 컨텐츠 */}
        {isLoading ? (
          <div className="flex justify-center p-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>
        ) : (
          <>
            {/* 기본정보 탭 */}
            {activeTab === 'OVERVIEW' && customer && (
              <div className="space-y-3">
                {/* 기본정보 서브탭 */}
                <div ref={subTabRef} className="bg-white rounded-lg shadow-sm p-2 flex items-center gap-1 overflow-x-auto scroll-smooth touch-pan-x overscroll-x-contain cursor-grab select-none [&::-webkit-scrollbar]:hidden [scrollbar-width:none] [-ms-overflow-style:none]">
                  {OVERVIEW_SUB_TABS.map(st => {
                    const isVendor = (isEditing ? editForm.customerChannel : customer.customerChannel) === 'VENDOR';
                    const disabled = st.id === 'partner' && !isVendor;
                    const isActive = activeSubTab === st.id;
                    return (
                      <button
                        key={st.id}
                        type="button"
                        disabled={disabled}
                        onClick={() => !disabled && changeSubTab(st.id)}
                        title={disabled ? '업체고객일 때 활성화됩니다' : ''}
                        className={`flex-shrink-0 px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                          isActive
                            ? 'bg-blue-100 text-blue-700'
                            : disabled
                              ? 'text-gray-400 cursor-not-allowed'
                              : 'text-gray-600 hover:bg-gray-100 cursor-pointer'
                        }`}
                      >
                        <i className={`${st.icon} mr-1.5`}></i>{st.label}
                      </button>
                    );
                  })}
                </div>

                {/* 기본 정보 */}
                {activeSubTab === 'basic' && (
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h3 className="text-lg font-bold text-gray-800 mb-6">기본 정보</h3>
                  {isEditing ? (
                    (() => {
                      // 편집 모드: 필드 순서/개수/저장 로직은 그대로 두고, 시각적으로만 5개 그룹으로 분리.
                      // 보기 모드의 compact 레이아웃과 대칭을 맞추기 위한 변경.
                      const toLocalDt = (v: string) => v ? v.replace(/\.000Z$/, '').slice(0, 16) : '';
                      const toDateOnly = (v: string) => v ? v.slice(0, 10) : '';
                      // datetime-local 값을 date 부분과 time 부분으로 분리
                      const splitDateTime = (v: string) => {
                        const s = toLocalDt(v);
                        if (!s) return { date: '', time: '' };
                        const [date, time] = s.split('T');
                        return { date: date || '', time: time || '' };
                      };
                      // date/time 파츠를 다시 하나의 ISO-like 문자열로 결합 (서버 저장 형식 유지)
                      const combineDateTime = (date: string, time: string) => {
                        if (!date && !time) return '';
                        if (!date) return ''; // date 없이 time만 있으면 저장 불가 — 빈 값 처리
                        return `${date}T${time || '00:00'}`;
                      };
                      const renderInputField = (field: { label: string; key: string; type?: string }) => {
                        const isDateTime = field.type === 'datetime-local';
                        const isDate = field.type === 'date';
                        // datetime-local은 "날짜 선택" + "시간 선택" 두 칸으로 분리해서 노출.
                        // 시/분/오전오후 3단 롤러 대신 브라우저 기본 date/time 피커를 각각 띄워서 훨씬 단순함.
                        if (isDateTime) {
                          const { date, time } = splitDateTime(editForm[field.key] || '');
                          const onDateChange = (newDate: string) => {
                            setEditForm({ ...editForm, [field.key]: combineDateTime(newDate, time) });
                          };
                          const onTimeChange = (newTime: string) => {
                            setEditForm({ ...editForm, [field.key]: combineDateTime(date, newTime) });
                          };
                          return (
                            <div key={field.key}>
                              <label className="block text-sm font-medium text-gray-500 mb-1">{field.label}</label>
                              <div className="flex gap-2">
                                <input
                                  type="date"
                                  value={date}
                                  onChange={(e) => onDateChange(e.target.value)}
                                  className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <input
                                  type="time"
                                  value={time}
                                  onChange={(e) => onTimeChange(e.target.value)}
                                  disabled={!date}
                                  title={!date ? '먼저 날짜를 선택해주세요' : ''}
                                  className="w-28 flex-shrink-0 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
                                />
                              </div>
                            </div>
                          );
                        }
                        const editValue = isDate ? toDateOnly(editForm[field.key] || '')
                          : (editForm[field.key] || '');
                        return (
                          <div key={field.key}>
                            <label className="block text-sm font-medium text-gray-500 mb-1">{field.label}</label>
                            <input
                              type={field.type || 'text'}
                              value={editValue}
                              onChange={(e) => setEditForm({ ...editForm, [field.key]: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        );
                      };
                      const personalFields = [
                        { label: '신랑 이름', key: 'groomName' },
                        { label: '신랑 연락처', key: 'groomPhone' },
                        { label: '신랑 이메일', key: 'groomEmail', type: 'email' },
                        { label: '신부 이름', key: 'brideName' },
                        { label: '신부 연락처', key: 'bridePhone' },
                        { label: '신부 이메일', key: 'brideEmail', type: 'email' },
                      ];
                      const eventFields = [
                        { label: '예식장소 (=본식장소)', key: 'weddingVenue' },
                        { label: '메이크업샵', key: 'makeupShopName' },
                        { label: '메이크업샵 시간', key: 'makeupShopDt', type: 'datetime-local' },
                      ];
                      const scheduleFields = [
                        { label: '리허설 촬영일', key: 'rehearsalDt', type: 'datetime-local' },
                        { label: '본식 촬영일', key: 'mainShootingDt', type: 'datetime-local' },
                        { label: '셀렉일', key: 'selectDt', type: 'datetime-local' },
                        { label: '예식일', key: 'weddingDt', type: 'datetime-local' },
                        { label: '발주(계약)일', key: 'orderDt', type: 'date' },
                      ];
                      return (
                        <div className="space-y-5">
                          {/* 인적사항 */}
                          <div>
                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">인적사항</div>
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                              {personalFields.map(renderInputField)}
                            </div>
                          </div>

                          {/* 예식·메이크업 */}
                          <div className="pt-5 border-t border-gray-100">
                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">예식·메이크업</div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {eventFields.map(renderInputField)}
                            </div>
                          </div>

                          {/* 일정 */}
                          <div className="pt-5 border-t border-gray-100">
                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">일정</div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {scheduleFields.map(renderInputField)}
                            </div>
                          </div>

                          {/* 분류 — 고객구분을 최상위로 두어 시각적 위계 부여.
                              업체/워킹 선택이 업체정보 노출 등 다른 영역에도 영향 주므로 가장 먼저 결정. */}
                          <div className="pt-5 border-t border-gray-100">
                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">분류</div>
                            <div className="space-y-4">
                              {/* 고객구분 — 최상위 분류, full-width + 강조 */}
                              <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                  고객구분 <span className="text-xs font-normal text-gray-400 ml-1">· 최상위 분류</span>
                                </label>
                                <select value={editForm.customerChannel || ''} onChange={(e) => setEditForm({ ...editForm, customerChannel: e.target.value })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                                  <option value="">선택</option>
                                  <option value="VENDOR">업체고객</option>
                                  <option value="WALKING">워킹고객</option>
                                </select>
                              </div>
                              {/* 유입경로 + 유입경로 상세: 서로 연동되는 필드라 짝으로 배치 */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-500 mb-1">유입경로</label>
                                  <select value={editForm.referralSource || ''}
                                    onChange={(e) => setEditForm({ ...editForm, referralSource: e.target.value, referralSourceDetail: '' })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                                    <option value="">선택</option>
                                    {REFERRAL_SOURCES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-500 mb-1">유입경로 상세</label>
                                  {(REFERRAL_SOURCE_DETAILS[editForm.referralSource] || []).length > 0 ? (
                                    <select value={editForm.referralSourceDetail || ''}
                                      onChange={(e) => setEditForm({ ...editForm, referralSourceDetail: e.target.value })}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                                      <option value="">선택</option>
                                      {(REFERRAL_SOURCE_DETAILS[editForm.referralSource] || []).map((d: any) => (
                                        <option key={d.value} value={d.value}>{d.label}</option>
                                      ))}
                                    </select>
                                  ) : (
                                    <input type="text" value={editForm.referralSourceDetail || ''}
                                      onChange={(e) => setEditForm({ ...editForm, referralSourceDetail: e.target.value })}
                                      placeholder={editForm.referralSource ? '직접 입력' : '유입경로를 먼저 선택하세요'}
                                      disabled={!editForm.referralSource}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50" />
                                  )}
                                </div>
                              </div>
                              {/* 촬영 범위: 서비스 유형 (인적 분류와는 독립) */}
                              <div>
                                <label className="block text-sm font-medium text-gray-500 mb-1">촬영 범위</label>
                                <select value={editForm.customerType || ''} onChange={(e) => setEditForm({ ...editForm, customerType: e.target.value })}
                                  className="w-full md:w-1/2 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                                  <option value="">선택</option>
                                  {CUSTOMER_TYPES.map(ct => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
                                </select>
                              </div>
                            </div>
                          </div>

                          {/* 메모 */}
                          <div className="pt-5 border-t border-gray-100">
                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">메모</div>
                            <div className="space-y-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-500 mb-1">메모</label>
                                <textarea value={editForm.memo || ''} onChange={(e) => setEditForm({ ...editForm, memo: e.target.value })} rows={4}
                                  placeholder="고객 관련 메모를 자유롭게 입력하세요 (예: 스타일 선호, 예민 포인트, 예산, 특이사항 등)"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-500 mb-1">본식 비고</label>
                                <textarea value={editForm.mainMemo || ''} onChange={(e) => setEditForm({ ...editForm, mainMemo: e.target.value })} rows={3}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-500 mb-1">해외고객 비고</label>
                                <textarea value={editForm.overseasNote || ''} onChange={(e) => setEditForm({ ...editForm, overseasNote: e.target.value })} rows={2}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()
                  ) : (() => {
                    const fmtD = (v?: string) => v ? new Date(v).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' }) : '';
                    const fmtDT = (v?: string) => v ? new Date(v).toLocaleString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
                    const hasAny = customer.groomName || customer.brideName || customer.groomPhone || customer.bridePhone ||
                      customer.groomEmail || customer.brideEmail ||
                      customer.weddingVenue || customer.makeupShopName || customer.makeupShopDt ||
                      customer.rehearsalDt || customer.mainShootingDt || customer.selectDt || customer.weddingDt || customer.orderDt ||
                      customer.referralSource || customer.customerType || customer.customerChannel ||
                      customer.memo || customer.mainMemo || customer.overseasNote;
                    return (
                      <div className="space-y-3 divide-y divide-gray-100">
                        {/* 신랑/신부 인적사항 */}
                        {(customer.groomName || customer.groomPhone || customer.groomEmail || customer.brideName || customer.bridePhone || customer.brideEmail) && (
                          <div className="space-y-2 pb-3">
                            {(customer.groomName || customer.groomPhone || customer.groomEmail) && (
                              <div className="flex items-baseline gap-4 text-sm">
                                <span className="text-gray-400 w-12 shrink-0">신랑</span>
                                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                                  {customer.groomName && <span className="font-semibold text-gray-900">{customer.groomName}</span>}
                                  {customer.groomPhone && <span className="text-gray-700">{customer.groomPhone}</span>}
                                  {customer.groomEmail && <span className="text-gray-500">{customer.groomEmail}</span>}
                                </div>
                              </div>
                            )}
                            {(customer.brideName || customer.bridePhone || customer.brideEmail) && (
                              <div className="flex items-baseline gap-4 text-sm">
                                <span className="text-gray-400 w-12 shrink-0">신부</span>
                                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                                  {customer.brideName && <span className="font-semibold text-gray-900">{customer.brideName}</span>}
                                  {customer.bridePhone && <span className="text-gray-700">{customer.bridePhone}</span>}
                                  {customer.brideEmail && <span className="text-gray-500">{customer.brideEmail}</span>}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        {/* 예식·메이크업 */}
                        {(customer.weddingVenue || customer.makeupShopName || customer.makeupShopDt) && (
                          <dl className="pt-3 grid gap-x-6 gap-y-1.5 text-sm grid-cols-[6rem_minmax(0,1fr)] lg:grid-cols-[6rem_minmax(0,1fr)_6rem_minmax(0,1fr)]">
                            {customer.weddingVenue && (<>
                              <dt className="text-gray-500">예식장소</dt>
                              <dd className="text-gray-900">{customer.weddingVenue}</dd>
                            </>)}
                            {customer.makeupShopName && (<>
                              <dt className="text-gray-500">메이크업샵</dt>
                              <dd className="text-gray-900">{customer.makeupShopName}</dd>
                            </>)}
                            {customer.makeupShopDt && (<>
                              <dt className="text-gray-500">메이크업 시간</dt>
                              <dd className="text-gray-900">{fmtDT(customer.makeupShopDt)}</dd>
                            </>)}
                          </dl>
                        )}
                        {/* 촬영/예식 일정 */}
                        {(customer.rehearsalDt || customer.mainShootingDt || customer.selectDt || customer.weddingDt || customer.orderDt) && (
                          <dl className="pt-3 grid gap-x-6 gap-y-1.5 text-sm grid-cols-[6rem_minmax(0,1fr)] lg:grid-cols-[6rem_minmax(0,1fr)_6rem_minmax(0,1fr)]">
                            {customer.rehearsalDt && (<>
                              <dt className="text-gray-500">리허설 촬영일</dt>
                              <dd className="text-gray-900 font-semibold">{fmtDT(customer.rehearsalDt)}</dd>
                            </>)}
                            {customer.mainShootingDt && (<>
                              <dt className="text-gray-500">본식 촬영일</dt>
                              <dd className="text-gray-900 font-semibold">{fmtDT(customer.mainShootingDt)}</dd>
                            </>)}
                            {customer.selectDt && (<>
                              <dt className="text-gray-500">셀렉일</dt>
                              <dd className="text-gray-900">{fmtDT(customer.selectDt)}</dd>
                            </>)}
                            {customer.weddingDt && (<>
                              <dt className="text-gray-500">예식일</dt>
                              <dd className="text-gray-900 font-semibold">{fmtDT(customer.weddingDt)}</dd>
                            </>)}
                            {customer.orderDt && (<>
                              <dt className="text-gray-500">발주(계약)일</dt>
                              <dd className="text-gray-700">{fmtD(customer.orderDt)}</dd>
                            </>)}
                          </dl>
                        )}
                        {/* 분류 */}
                        {(customer.referralSource || customer.customerType || customer.customerChannel) && (
                          <dl className="pt-3 grid gap-x-6 gap-y-1.5 text-sm grid-cols-[6rem_minmax(0,1fr)] lg:grid-cols-[6rem_minmax(0,1fr)_6rem_minmax(0,1fr)]">
                            {/* 고객구분 — 최상위 분류, 편집 모드와 동일한 위계로 최상단에 노출 */}
                            {customer.customerChannel && (<>
                              <dt className="text-gray-500">고객구분</dt>
                              <dd className="text-gray-900 font-semibold">{customer.customerChannel === 'VENDOR' ? '업체고객' : '워킹고객'}</dd>
                            </>)}
                            {customer.referralSource && (<>
                              <dt className="text-gray-500">유입경로</dt>
                              <dd className="text-gray-900">
                                {customer.referralSource}
                                {customer.referralSourceDetail && <span className="text-gray-500 ml-1.5">· {customer.referralSourceDetail}</span>}
                              </dd>
                            </>)}
                            {customer.customerType && (<>
                              <dt className="text-gray-500">촬영 범위</dt>
                              <dd className="text-gray-900">{CUSTOMER_TYPES.find(ct => ct.value === customer.customerType)?.label || '-'}</dd>
                            </>)}
                          </dl>
                        )}
                        {/* 메모 */}
                        {(customer.memo || customer.mainMemo || customer.overseasNote) && (
                          <div className="pt-3 space-y-3">
                            {customer.memo && (
                              <div>
                                <div className="text-xs text-gray-500 mb-1">메모</div>
                                <p className="text-sm text-gray-900 whitespace-pre-wrap">{customer.memo}</p>
                              </div>
                            )}
                            {customer.mainMemo && (
                              <div>
                                <div className="text-xs text-gray-500 mb-1">본식 비고</div>
                                <p className="text-sm text-gray-900 whitespace-pre-wrap">{customer.mainMemo}</p>
                              </div>
                            )}
                            {customer.overseasNote && (
                              <div>
                                <div className="text-xs text-gray-500 mb-1">해외고객 비고</div>
                                <p className="text-sm text-gray-900 whitespace-pre-wrap">{customer.overseasNote}</p>
                              </div>
                            )}
                          </div>
                        )}
                        {!hasAny && (
                          <p className="text-sm text-gray-400 text-center py-8">
                            등록된 기본 정보가 없습니다. 상단 <span className="font-medium">수정</span> 버튼으로 입력해주세요.
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </div>

                )}

                {/* 촬영자 (1, 2) — customerType 기준으로 노출 필드 결정 */}
                {activeSubTab === 'photographers' && (() => {
                  const effectiveType = isEditing ? editForm.customerType : customer.customerType;
                  const allPhotographerFields = [
                    { label: '리허설 촬영자 1', key: 'rehearsalPhotographerIdx', relKey: 'rehearsalPhotographer', scope: 'REHEARSAL' as const },
                    { label: '리허설 촬영자 2', key: 'rehearsalPhotographer2Idx', relKey: 'rehearsalPhotographer2', scope: 'REHEARSAL' as const },
                    { label: '본식 촬영자 1', key: 'mainPhotographerIdx', relKey: 'mainPhotographer', scope: 'MAIN' as const },
                    { label: '본식 촬영자 2', key: 'mainPhotographer2Idx', relKey: 'mainPhotographer2', scope: 'MAIN' as const },
                  ];
                  const visiblePhotographerFields = allPhotographerFields.filter(f => {
                    if (effectiveType === 'REHEARSAL') return f.scope === 'REHEARSAL';
                    if (effectiveType === 'MAIN') return f.scope === 'MAIN';
                    return true;
                  });
                  return (
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h3 className="text-lg font-bold text-gray-800 mb-6">촬영자</h3>
                  <div className="grid grid-cols-2 gap-6">
                    {visiblePhotographerFields.map(f => {
                      const user = customer[f.relKey];
                      return (
                      <div key={f.key}>
                        <label className="block text-sm font-medium text-gray-500 mb-1">{f.label}</label>
                        {isEditing ? (
                          <select value={editForm[f.key] || ''}
                            onChange={(e) => setEditForm({ ...editForm, [f.key]: e.target.value ? Number(e.target.value) : null })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                            <option value="">선택</option>
                            {(staffList as any[]).map((s: any) => <option key={s.userIdx} value={s.userIdx}>{s.name}</option>)}
                          </select>
                        ) : (
                          <div className="flex items-start gap-2">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-gray-900">{user?.name || '-'}</p>
                                {user?.phoneNumber && (
                                  <button
                                    type="button"
                                    onClick={() => sendSmsToUser(user, `${f.label} ${user.name}`)}
                                    title={`${user.name} (${user.phoneNumber}) 에게 문자 보내기`}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-indigo-50 text-indigo-700 border border-indigo-100 rounded-md hover:bg-indigo-100 cursor-pointer"
                                  >
                                    <i className="ri-message-2-line"></i>문자
                                  </button>
                                )}
                              </div>
                              {user?.phoneNumber && (
                                <p className="text-xs text-gray-400 mt-0.5">{user.phoneNumber}</p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                </div>
                  );
                })()}

                {/* 업체(파트너) 정보 — 업체고객일 때만 노출 */}
                {activeSubTab === 'partner' && (isEditing ? editForm.customerChannel === 'VENDOR' : customer.customerChannel === 'VENDOR') && (
                  <div className="bg-white rounded-lg shadow-sm p-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-6">업체 정보</h3>
                    <div className="grid grid-cols-2 gap-6">
                      {[
                        { label: '업체명', key: 'partnerVendorName' },
                        { label: '업체 담당자', key: 'partnerStaffName' },
                        { label: '담당자 연락처', key: 'partnerStaffPhone' },
                      ].map(f => (
                        <div key={f.key}>
                          <label className="block text-sm font-medium text-gray-500 mb-1">{f.label}</label>
                          {isEditing ? (
                            <input type="text" value={editForm[f.key] || ''}
                              onChange={(e) => setEditForm({ ...editForm, [f.key]: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                          ) : (
                            <p className="text-gray-900">{customer[f.key] || '-'}</p>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="mt-6">
                      <label className="block text-sm font-medium text-gray-500 mb-1">업체 서비스 내역</label>
                      {isEditing ? (
                        <textarea value={editForm.partnerServiceNote || ''}
                          onChange={(e) => setEditForm({ ...editForm, partnerServiceNote: e.target.value })} rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                      ) : (
                        <p className="text-gray-900 whitespace-pre-wrap">{customer.partnerServiceNote || '-'}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* 셀렉 정보 */}
                {activeSubTab === 'select' && (
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h3 className="text-lg font-bold text-gray-800 mb-6">셀렉 정보</h3>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">원본 CD 출고일</label>
                      {isEditing ? (
                        <input type="date" value={(editForm.originalCdShipDt || '').slice(0, 10)}
                          onChange={(e) => setEditForm({ ...editForm, originalCdShipDt: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      ) : (
                        <p className="text-gray-900">{customer.originalCdShipDt ? new Date(customer.originalCdShipDt).toLocaleDateString('ko-KR') : '-'}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">출고 구분</label>
                      {isEditing ? (
                        <select value={editForm.shipMethod || ''} onChange={(e) => setEditForm({ ...editForm, shipMethod: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                          <option value="">선택</option>
                          <option value="PICKUP">방문</option>
                          <option value="COURIER">택배</option>
                        </select>
                      ) : (
                        <p className="text-gray-900">{customer.shipMethod === 'PICKUP' ? '방문' : customer.shipMethod === 'COURIER' ? '택배' : '-'}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">택배 구분</label>
                      {isEditing ? (
                        <select value={editForm.courierPayment || ''}
                          onChange={(e) => setEditForm({ ...editForm, courierPayment: e.target.value })}
                          disabled={editForm.shipMethod !== 'COURIER'}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50">
                          <option value="">선택</option>
                          <option value="PREPAID">선불</option>
                          <option value="COLLECT">착불</option>
                        </select>
                      ) : (
                        <p className="text-gray-900">{customer.courierPayment === 'PREPAID' ? '선불' : customer.courierPayment === 'COLLECT' ? '착불' : '-'}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">셀렉 구분</label>
                      {isEditing ? (
                        <select value={editForm.selectMethod || ''} onChange={(e) => setEditForm({ ...editForm, selectMethod: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                          <option value="">선택</option>
                          <option value="VISIT">방문셀렉</option>
                          <option value="EMAIL">이메일셀렉</option>
                          <option value="AUTO">임의셀렉</option>
                        </select>
                      ) : (
                        <p className="text-gray-900">{({ VISIT: '방문셀렉', EMAIL: '이메일셀렉', AUTO: '임의셀렉' } as any)[customer.selectMethod] || '-'}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">셀렉 내용</label>
                      {isEditing ? (
                        <select value={editForm.selectContent || ''} onChange={(e) => setEditForm({ ...editForm, selectContent: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                          <option value="">선택</option>
                          <option value="DIRECT">직접</option>
                          <option value="AUTO">임의</option>
                        </select>
                      ) : (
                        <p className="text-gray-900">{({ DIRECT: '직접', AUTO: '임의' } as any)[customer.selectContent] || '-'}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">셀렉 담당자</label>
                      {isEditing ? (
                        <select value={editForm.selectManagerIdx || ''}
                          onChange={(e) => setEditForm({ ...editForm, selectManagerIdx: e.target.value ? Number(e.target.value) : null })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                          <option value="">선택</option>
                          {(staffList as any[]).map((s: any) => <option key={s.userIdx} value={s.userIdx}>{s.name}</option>)}
                        </select>
                      ) : (
                        <p className="text-gray-900">{customer.selectManager?.name || '-'}</p>
                      )}
                    </div>
                  </div>
                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-500 mb-1">셀렉 비고</label>
                    {isEditing ? (
                      <textarea value={editForm.selectMemo || ''}
                        onChange={(e) => setEditForm({ ...editForm, selectMemo: e.target.value })} rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                    ) : (
                      <p className="text-gray-900 whitespace-pre-wrap">{customer.selectMemo || '-'}</p>
                    )}
                  </div>
                </div>
                )}

              </div>
            )}

            {/* 계약정보 탭 */}
            {activeTab === 'CONTRACTS' && (
              <div className="bg-white rounded-lg shadow-sm">
                <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="text-lg font-bold text-gray-800">계약 목록</h3>
                  <button onClick={openContractModal}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm flex items-center gap-2 cursor-pointer">
                    <i className="ri-add-line"></i> 신규 계약
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">계약번호</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">계약일</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">발주업체</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">품목</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">상품명</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">입출고 상태</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">금액</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">계약상태</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {(tabData?.contracts || []).length === 0 ? (
                        <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-500">계약 내역이 없습니다.</td></tr>
                      ) : (tabData?.contracts || []).map((c: any) => {
                        const contractIdx = c.photoContractIdx || c.photo_contract_idx;
                        const contractNo = c.contractNo || c.contract_no || '-';
                        const contractType = c.contractType || c.contract_type || '';
                        const finalAmt = c.finalAmount || c.final_amount || 0;
                        const status = c.status || '';
                        const items = contractItemsMap[contractIdx] || [];
                        const contractTypeLabel = CONTRACT_TYPES.find(t => t.value === contractType)?.label || contractType;

                        return (
                          <React.Fragment key={contractIdx}>
                            {/* 계약 헤더 행 */}
                            <tr className="bg-gray-50/50 hover:bg-gray-100/50 cursor-pointer" onClick={() => navigate(`/photo-studio/contract-detail?id=${contractIdx}`)}>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <button onClick={() => navigate(`/photo-studio/contract-detail?id=${contractIdx}`)}
                                    className="text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer">{contractNo}</button>
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${contractType === 'REHEARSAL_AND_MAIN' ? 'bg-orange-100 text-orange-700' : contractType === 'CUSTOM' ? 'bg-blue-100 text-blue-700' : 'bg-teal-100 text-teal-700'}`}>{contractTypeLabel}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">{(() => { const dt = c.contractDt || c.contract_dt; if (!dt) return '-'; try { return new Date(dt).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' }); } catch { return dt; } })()}</td>
                              <td className="px-4 py-3 text-xs text-gray-600">{c.partnerVendorName || c.partner_vendor_name || '-'}</td>
                              <td className="px-4 py-3"></td>
                              <td className="px-4 py-3"></td>
                              <td className="px-4 py-3"></td>
                              <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">{Number(finalAmt).toLocaleString()}원</td>
                              <td className="px-4 py-3">
                                <select value={status} onClick={(e) => e.stopPropagation()} onChange={async (e) => {
                                  try {
                                    await contractApi.update(contractIdx, { status: e.target.value });
                                    fetchData();
                                  } catch (err: any) { alert(`변경 실패: ${err.message}`); }
                                }} className={`text-xs px-2 py-1 rounded-full border-0 cursor-pointer focus:outline-none ${status === 'ACTIVE' ? 'bg-green-100 text-green-700' : status === 'COMPLETED' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}`}>
                                  {CONTRACT_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                </select>
                              </td>
                            </tr>
                            {/* 품목 행들 */}
                            {items.map((item: any, idx: number) => {
                              const itemCategory = item.itemCategory || '';
                              const productName = item.productName || '-';
                              const workStatus = item.workStatus || '';
                              const categoryLabel = ITEM_CATEGORIES.find((ic: any) => ic.value === itemCategory)?.label || itemCategory;
                              return (
                                <tr key={item.photoContractItemIdx || idx} className="hover:bg-blue-50/30">
                                  <td className="px-4 py-2.5 pl-12"></td>
                                  <td className="px-4 py-2.5"></td>
                                  <td className="px-4 py-2.5"></td>
                                  <td className="px-4 py-2.5">
                                    <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">{categoryLabel}</span>
                                  </td>
                                  <td className="px-4 py-2.5 text-sm text-gray-900">{productName}</td>
                                  <td className="px-4 py-2.5">
                                    <select
                                      value={workStatus}
                                      onChange={(e) => handleWorkStatusChange(item, e.target.value, contractIdx)}
                                      className="text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white cursor-pointer">
                                      {(item.workType === 'DATA' ? DATA_PIPELINE : PRODUCTION_PIPELINE).map((stage: string) => (
                                        <option key={stage} value={stage}>{stage}</option>
                                      ))}
                                    </select>
                                  </td>
                                  <td className="px-4 py-2.5 text-sm text-gray-500 text-right">{Number(item.unitPrice || 0).toLocaleString()}원</td>
                                  <td className="px-4 py-2.5"></td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* 일정정보 탭 */}
            {activeTab === 'SCHEDULES' && (
              <div className="bg-white rounded-lg shadow-sm">
                <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="text-lg font-bold text-gray-800">일정 목록</h3>
                  <button onClick={openScheduleModal}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm flex items-center gap-2 cursor-pointer">
                    <i className="ri-add-line"></i> 일정 등록
                  </button>
                </div>
                <div className="divide-y divide-gray-200">
                  {(tabData?.schedules || []).length === 0 ? (
                    <div className="p-12 text-center text-gray-400">
                      <i className="ri-calendar-line text-4xl block mb-2"></i>
                      등록된 일정이 없습니다.
                      <button onClick={openScheduleModal} className="block mx-auto mt-3 text-blue-600 hover:text-blue-800 text-sm font-medium cursor-pointer">
                        + 첫 일정 등록하기
                      </button>
                    </div>
                  ) : (tabData?.schedules || []).map((s: any) => {
                    const startDate = s.startDt || s.start_dt;
                    const endDate = s.endDt || s.end_dt;
                    const formatScheduleDate = (dt: string) => {
                      if (!dt) return '';
                      try {
                        const d = new Date(dt);
                        if (isNaN(d.getTime())) return '';
                        return d.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
                      } catch { return ''; }
                    };
                    return (
                      <div key={s.photoScheduleIdx || s.photo_schedule_idx} className="p-4 flex items-center gap-4 hover:bg-gray-50 group">
                        <div className={`w-3 h-3 rounded-full flex-shrink-0 ${SCHEDULE_CATEGORY_COLORS[s.scheduleCategory || s.schedule_category]?.split(' ')[0] || 'bg-gray-400'}`}></div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${SCHEDULE_CATEGORY_COLORS[s.scheduleCategory || s.schedule_category] || 'bg-gray-100 text-gray-700'}`}>
                              {SCHEDULE_CATEGORIES.find(c => c.value === (s.scheduleCategory || s.schedule_category))?.label || s.scheduleCategory || s.schedule_category}
                            </span>
                            <span className="text-sm font-medium text-gray-900">{s.title}</span>
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {formatScheduleDate(startDate)}{endDate && endDate !== startDate ? ` ~ ${formatScheduleDate(endDate)}` : ''}
                          </div>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${SCHEDULE_STATUS_COLORS[s.status] || 'bg-gray-100 text-gray-700'}`}>{s.status}</span>
                        <button onClick={async () => {
                          if (!confirm('이 일정을 삭제하시겠습니까?')) return;
                          try {
                            await scheduleApi.delete(s.photoScheduleIdx || s.photo_schedule_idx);
                            fetchData();
                          } catch (err: any) { alert(`삭제 실패: ${err.message}`); }
                        }} className="text-gray-300 hover:text-red-500 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity">
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 결제정보 탭 */}
            {activeTab === 'PAYMENTS' && (() => {
              const payments = (tabData?.payments || []);
              const totalPaid = payments.reduce((sum: number, p: any) => sum + Number(p.amount || p.paid_amount || 0), 0);
              const contracts = tabData?.contracts || [];
              const totalContract = contracts.reduce((sum: number, c: any) => sum + Number(c.finalAmount || c.final_amount || 0), 0);
              const remaining = totalContract - totalPaid;
              const getPaymentType = (t: string) => ({ FULL: '일시납부', CONTRACT: '계약금', INTERMEDIATE: '중도금', BALANCE: '잔금', ETC: '기타' }[t] || t || '-');
              // 계약번호 매핑
              const contractNoMap: Record<number, string> = {};
              contracts.forEach((c: any) => {
                const idx = c.photoContractIdx || c.photo_contract_idx;
                const no = c.contractNo || c.contract_no || '';
                if (idx) contractNoMap[idx] = no;
              });
              const getPaymentMethod = (m: string) => ({ CARD: '카드', TRANSFER: '계좌이체', CASH: '현금' }[m] || m || '-');
              const getPaymentStatus = (s: string) => ({ COMPLETED: '완료', SCHEDULED: '예정', REFUNDED: '환불', CANCELLED: '취소' }[s] || s || '-');
              const formatDt = (dt: string) => { if (!dt) return '-'; try { return new Date(dt).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' }); } catch { return dt; } };

              return (
                <div className="space-y-4">
                  {/* 결제 요약 */}
                  {totalContract > 0 && (
                    <div className="bg-white rounded-lg shadow-sm p-5">
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <div className="text-xs text-gray-500 mb-1">계약 총액</div>
                          <div className="text-lg font-bold text-gray-900">{totalContract.toLocaleString()}원</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500 mb-1">결제 완료</div>
                          <div className="text-lg font-bold text-blue-600">{totalPaid.toLocaleString()}원</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500 mb-1">미납금</div>
                          <div className={`text-lg font-bold ${remaining > 0 ? 'text-red-600' : 'text-green-600'}`}>{remaining.toLocaleString()}원</div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                      <h3 className="text-lg font-bold text-gray-800">결제 내역</h3>
                      <button onClick={openPaymentModal}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm flex items-center gap-2 cursor-pointer">
                        <i className="ri-add-line"></i> 결제 등록
                      </button>
                    </div>
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">계약</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">결제일</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">결제유형</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">금액</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">결제수단</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">상태</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">메모</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {payments.length === 0 ? (
                          <tr><td colSpan={7} className="px-4 py-12 text-center text-gray-500">결제 내역이 없습니다.</td></tr>
                        ) : payments.map((p: any) => {
                          const paymentType = p.paymentType || p.payment_type || '';
                          const paymentMethod = p.paymentMethod || p.payment_method || '';
                          const paidDt = p.paidDt || p.paid_dt || '';
                          const status = p.status || '';
                          return (
                            <tr key={p.photoPaymentIdx || p.photo_payment_idx} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedPayment(p)}>
                              <td className="px-4 py-4 text-xs text-blue-600 font-medium">{contractNoMap[p.photoContractIdx || p.photo_contract_idx] || '-'}</td>
                              <td className="px-4 py-4 text-sm text-gray-700">{formatDt(paidDt)}</td>
                              <td className="px-4 py-4"><span className={`text-xs px-2 py-1 rounded-full ${PAYMENT_TYPE_COLORS[paymentType] || 'bg-gray-100 text-gray-700'}`}>{getPaymentType(paymentType)}</span></td>
                              <td className="px-4 py-4 text-sm font-semibold text-gray-900 text-right">{Number(p.amount || 0).toLocaleString()}원</td>
                              <td className="px-4 py-4 text-sm text-gray-700">{getPaymentMethod(paymentMethod)}</td>
                              <td className="px-4 py-4"><span className={`text-xs px-2 py-1 rounded-full ${PAYMENT_STATUS_COLORS[status] || 'bg-gray-100 text-gray-700'}`}>{getPaymentStatus(status)}</span></td>
                              <td className="px-4 py-4 text-sm text-gray-500">{p.memo || '-'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}

            {/* 문자전송 탭 */}
            {activeTab === 'SMS' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
                    {([
                      { key: 'history' as const, label: '발송 내역', icon: 'ri-list-check-2' },
                      { key: 'send' as const, label: '문자 발송', icon: 'ri-send-plane-line' },
                      { key: 'templates' as const, label: '템플릿 관리', icon: 'ri-file-copy-2-line' },
                    ]).map(tab => (
                      <button key={tab.key} onClick={() => setSmsTab(tab.key)}
                        className={`px-4 py-1.5 rounded-md text-sm font-medium flex items-center gap-1.5 cursor-pointer transition-colors ${
                          smsTab === tab.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                        }`}>
                        <i className={tab.icon}></i> {tab.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* 발송 내역 */}
                {smsTab === 'history' && (
                  <div>
                    {smsLogs.length === 0 ? (
                      <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400">
                        <i className="ri-message-2-line text-4xl block mb-2"></i>발송된 문자가 없습니다
                      </div>
                    ) : (
                      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-gray-50 text-gray-500 text-xs">
                                <th className="px-4 py-3 text-left font-medium">발송일시</th>
                                <th className="px-4 py-3 text-left font-medium">유형</th>
                                <th className="px-4 py-3 text-left font-medium">수신번호</th>
                                <th className="px-4 py-3 text-left font-medium">제목</th>
                                <th className="px-4 py-3 text-left font-medium">내용</th>
                                <th className="px-4 py-3 text-center font-medium">상태</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {smsLogs.map((log: any) => (
                                <tr key={log.photoNotificationLogIdx} className="hover:bg-blue-50/30 transition-colors">
                                  <td className="px-4 py-3 text-xs text-gray-600">
                                    {log.sentDt ? new Date(log.sentDt).toLocaleString('ko-KR') : '-'}
                                  </td>
                                  <td className="px-4 py-3">
                                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                                      {log.notificationType === 'SHOOTING_REMINDER' ? '촬영알림' :
                                       log.notificationType === 'SELECT_REMINDER' ? '셀렉알림' :
                                       log.notificationType === 'GENERAL' ? '일반' : log.notificationType}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-xs text-gray-600">{log.recipientPhone || '-'}</td>
                                  <td className="px-4 py-3 text-xs text-gray-800 font-medium">{log.title || '-'}</td>
                                  <td className="px-4 py-3 text-xs text-gray-600 max-w-[300px] truncate" title={log.content}>
                                    {log.content || '-'}
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                      log.status === 'SENT' ? 'bg-green-100 text-green-700' :
                                      log.status === 'FAILED' ? 'bg-red-100 text-red-700' :
                                      'bg-yellow-100 text-yellow-700'
                                    }`}>
                                      {log.status === 'SENT' ? '발송완료' : log.status === 'FAILED' ? '실패' : '대기중'}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* 문자 발송 */}
                {smsTab === 'send' && (
                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <form onSubmit={handleSendSms} className="space-y-4 max-w-xl">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">수신번호 *</label>
                        <div className="space-y-2">
                          <div className="flex gap-2">
                            <input type="tel" value={smsForm.to} onChange={(e) => setSmsForm({ ...smsForm, to: e.target.value })}
                              placeholder="010-0000-0000" required
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
                            {customer?.groomPhone && (
                              <button type="button" onClick={() => setSmsForm({ ...smsForm, to: customer.groomPhone })}
                                className="px-3 py-2 text-xs bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 cursor-pointer whitespace-nowrap">
                                신랑 {customer.groomPhone}
                              </button>
                            )}
                            {customer?.bridePhone && (
                              <button type="button" onClick={() => setSmsForm({ ...smsForm, to: customer.bridePhone })}
                                className="px-3 py-2 text-xs bg-pink-50 text-pink-600 rounded-lg hover:bg-pink-100 cursor-pointer whitespace-nowrap">
                                신부 {customer.bridePhone}
                              </button>
                            )}
                          </div>
                          {(() => {
                            const photographers = [
                              { role: '리허설1', user: customer?.rehearsalPhotographer },
                              { role: '리허설2', user: customer?.rehearsalPhotographer2 },
                              { role: '본식1', user: customer?.mainPhotographer },
                              { role: '본식2', user: customer?.mainPhotographer2 },
                            ].filter(p => p.user?.phoneNumber);
                            if (photographers.length === 0) return null;
                            return (
                              <div className="flex flex-wrap gap-1.5 items-center">
                                <span className="text-xs text-gray-500 mr-1">촬영자</span>
                                {photographers.map(p => (
                                  <button
                                    key={p.role}
                                    type="button"
                                    onClick={() => setSmsForm({ ...smsForm, to: p.user.phoneNumber })}
                                    className="px-2.5 py-1.5 text-xs bg-indigo-50 text-indigo-700 border border-indigo-100 rounded-md hover:bg-indigo-100 cursor-pointer whitespace-nowrap"
                                  >
                                    {p.role} {p.user.name} · {p.user.phoneNumber}
                                  </button>
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">템플릿 불러오기</label>
                        <div className="flex gap-1.5 flex-wrap">
                          {smsTemplates.map((t: any) => (
                            <button key={t.photoSmsTemplateIdx} type="button" onClick={() => applyTemplate(t)}
                              className="text-xs px-2.5 py-1.5 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 cursor-pointer">
                              {t.templateName}
                            </button>
                          ))}
                          {smsTemplates.length === 0 && <span className="text-xs text-gray-400">등록된 템플릿이 없습니다</span>}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">제목</label>
                        <input type="text" value={smsForm.title} onChange={(e) => setSmsForm({ ...smsForm, title: e.target.value })}
                          placeholder="문자 제목 (선택)" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">내용 *</label>
                        <textarea value={smsForm.text} onChange={(e) => setSmsForm({ ...smsForm, text: e.target.value })}
                          rows={5} required placeholder="문자 내용을 입력하세요"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none" />
                        <p className="text-xs text-gray-400 mt-1">{smsForm.text.length} / 90 bytes (90byte 초과 시 LMS로 전송)</p>
                      </div>
                      <div className="flex justify-end gap-3 pt-2">
                        <button type="submit"
                          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium cursor-pointer text-sm flex items-center gap-1.5">
                          <i className="ri-send-plane-line"></i> 발송
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* 템플릿 관리 */}
                {smsTab === 'templates' && (
                  <div>
                    <div className="flex justify-end mb-3">
                      <button onClick={() => { setEditingTemplate(null); setTemplateForm({ templateName: '', title: '', content: '', templateType: 'GENERAL' }); setShowTemplateModal(true); }}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm flex items-center gap-1.5 cursor-pointer">
                        <i className="ri-add-line"></i> 템플릿 추가
                      </button>
                    </div>
                    {smsTemplates.length === 0 ? (
                      <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400">
                        <i className="ri-file-copy-2-line text-4xl block mb-2"></i>등록된 템플릿이 없습니다
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {smsTemplates.map((t: any) => (
                          <div key={t.photoSmsTemplateIdx} className="bg-white rounded-xl border border-gray-200 p-4">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <h4 className="font-semibold text-gray-800 text-sm">{t.templateName}</h4>
                                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 mt-1 inline-block">
                                  {t.templateType === 'SHOOTING_REMINDER' ? '촬영알림' :
                                   t.templateType === 'SELECT_REMINDER' ? '셀렉알림' :
                                   t.templateType === 'GENERAL' ? '일반' : t.templateType}
                                </span>
                              </div>
                              <div className="flex gap-1">
                                <button onClick={() => { setEditingTemplate(t); setTemplateForm({ templateName: t.templateName, title: t.title || '', content: t.content, templateType: t.templateType }); setShowTemplateModal(true); }}
                                  className="text-xs text-blue-600 hover:text-blue-800 p-1 hover:bg-blue-50 rounded cursor-pointer"><i className="ri-edit-line"></i></button>
                                <button onClick={() => handleDeleteTemplate(t)}
                                  className="text-xs text-red-500 hover:text-red-700 p-1 hover:bg-red-50 rounded cursor-pointer"><i className="ri-delete-bin-line"></i></button>
                              </div>
                            </div>
                            {t.title && <p className="text-xs text-gray-500 mb-1">제목: {t.title}</p>}
                            <p className="text-xs text-gray-600 whitespace-pre-wrap line-clamp-3">{t.content}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* 일정 등록 모달 */}
        {showScheduleModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex justify-between items-center z-10">
                <h2 className="text-lg font-bold text-gray-900">일정 등록</h2>
                <button onClick={() => setShowScheduleModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-xl"></i></button>
              </div>
              <form onSubmit={handleCreateSchedule} className="p-5 space-y-2.5">
                {scheduleItems.map((item, idx) => {
                  const catInfo = SCHEDULE_CATEGORIES.find(c => c.value === item.scheduleCategory);
                  const isSelect = item.scheduleCategory === 'SELECT';
                  return (
                    <div key={idx} className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                      <div className={`flex items-center justify-between px-4 py-2 ${catInfo?.color || 'bg-gray-500'} bg-opacity-10`}>
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${catInfo?.color || 'bg-gray-400'}`} />
                          <select value={item.scheduleCategory} onChange={(e) => handleScheduleItemChange(idx, 'scheduleCategory', e.target.value)}
                            className="text-xs font-semibold text-gray-700 bg-transparent border-0 focus:outline-none focus:ring-0 cursor-pointer py-0 pl-0 pr-5">
                            {SCHEDULE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                          </select>
                        </div>
                        {scheduleItems.length > 1 && (
                          <button type="button" onClick={() => removeScheduleItem(idx)} className="text-gray-400 hover:text-red-500 cursor-pointer"><i className="ri-close-line text-sm"></i></button>
                        )}
                      </div>
                      <div className="px-4 py-3 space-y-2.5">
                        <input type="text" value={item.title} onChange={(e) => handleScheduleItemChange(idx, 'title', e.target.value)}
                          placeholder={`${customer?.groomName || customer?.brideName || ''} ${catInfo?.label || ''}`}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-gray-300" />
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-[10px] text-gray-400 mb-0.5">시작</label>
                            <input type="datetime-local" value={item.startDt} onChange={(e) => handleScheduleItemChange(idx, 'startDt', e.target.value)}
                              className="w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500" />
                          </div>
                          <div>
                            <label className="block text-[10px] text-gray-400 mb-0.5">종료</label>
                            <input type="datetime-local" value={item.endDt} onChange={(e) => handleScheduleItemChange(idx, 'endDt', e.target.value)}
                              className="w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500" />
                          </div>
                        </div>
                        {isSelect && selectRooms.length > 0 && (
                          <div>
                            <label className="block text-[10px] text-gray-400 mb-0.5">셀렉실</label>
                            <select value={item.roomIdx || ''} onChange={(e) => handleScheduleItemChange(idx, 'roomIdx', e.target.value)}
                              className="w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500">
                              <option value="">선택 안함</option>
                              {selectRooms.map((r: any) => <option key={r.roomIdx} value={r.roomIdx}>{r.name}</option>)}
                            </select>
                          </div>
                        )}
                        <input type="text" value={item.memo} onChange={(e) => handleScheduleItemChange(idx, 'memo', e.target.value)}
                          placeholder="메모" className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-gray-300" />
                      </div>
                    </div>
                  );
                })}
                <button type="button" onClick={addScheduleItem}
                  className="w-full py-2.5 border border-dashed border-gray-300 rounded-xl text-xs text-gray-400 hover:border-blue-400 hover:text-blue-600 cursor-pointer transition-colors flex items-center justify-center gap-1">
                  <i className="ri-add-line"></i> 일정 추가
                </button>
                <div className="flex justify-end gap-2.5 pt-3 border-t border-gray-100">
                  <button type="button" onClick={() => setShowScheduleModal(false)} className="px-5 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer text-sm text-gray-600">취소</button>
                  <button type="submit" className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer text-sm font-medium">
                    {scheduleItems.length > 1 ? `${scheduleItems.length}건 등록` : '등록'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* 빠른 문자 전송 모달 (촬영자/담당자 등에게 현재 화면에서 바로 발송) */}
        {showSmsModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
                <h2 className="text-lg font-bold text-gray-900">문자 전송</h2>
                <button type="button" onClick={closeSmsModal} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
              <form onSubmit={handleSendSms} className="p-5 space-y-3">
                {(() => {
                  // 같은 고객의 연락처 후보 (신랑/신부 + 촬영자 1~4 + 담당자 + 셀렉담당)
                  const candidates: Array<{ to: string; name: string; label: string }> = [];
                  if (customer?.groomPhone) candidates.push({ to: customer.groomPhone, name: customer.groomName || '', label: '신랑' });
                  if (customer?.bridePhone) candidates.push({ to: customer.bridePhone, name: customer.brideName || '', label: '신부' });
                  const photographers = [
                    { user: customer?.rehearsalPhotographer, label: '리허설1' },
                    { user: customer?.rehearsalPhotographer2, label: '리허설2' },
                    { user: customer?.mainPhotographer, label: '본식1' },
                    { user: customer?.mainPhotographer2, label: '본식2' },
                  ];
                  for (const p of photographers) {
                    if (p.user?.phoneNumber) candidates.push({ to: p.user.phoneNumber, name: p.user.name, label: p.label });
                  }
                  if (customer?.assignedStaff?.phoneNumber) candidates.push({ to: customer.assignedStaff.phoneNumber, name: customer.assignedStaff.name, label: '담당자' });
                  if (customer?.selectManager?.phoneNumber) candidates.push({ to: customer.selectManager.phoneNumber, name: customer.selectManager.name, label: '셀렉담당' });
                  const addedPhones = new Set(smsRecipients.map(r => r.to));
                  return (
                    <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-indigo-600">받는 사람 ({smsRecipients.length}명)</span>
                      </div>
                      {smsRecipients.length === 0 ? (
                        <div className="text-sm text-gray-400">아래에서 수신자를 추가해주세요.</div>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {smsRecipients.map(r => (
                            <div key={r.to} className="inline-flex items-center gap-1.5 bg-white border border-indigo-200 rounded-full pl-2.5 pr-1 py-0.5 text-xs">
                              <span className="text-gray-700 font-medium">{r.label || r.name || ''}</span>
                              <span className="text-gray-400">{r.to}</span>
                              <button type="button" onClick={() => setSmsRecipients(smsRecipients.filter(x => x.to !== r.to))}
                                className="w-4 h-4 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 cursor-pointer" aria-label="제거">
                                <i className="ri-close-line text-xs"></i>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      {candidates.length > 0 && (
                        <div className="flex flex-wrap gap-1 items-center pt-1 border-t border-indigo-100">
                          <span className="text-xs text-indigo-500 mr-1">+ 추가</span>
                          {candidates.map(c => {
                            // 같은 번호가 이미 수신자 리스트에 있으면 비활성 pill로 표시 (중복 발송 방지).
                            // 숨기지 않는 이유: 사용자가 "왜 본식1이 없지?" 하고 혼란스러워하지 않게 명시.
                            const alreadyAdded = addedPhones.has(c.to);
                            if (alreadyAdded) {
                              return (
                                <span key={`${c.label}-${c.to}`}
                                  title={`같은 번호(${c.to})가 이미 추가되어 있어 중복 발송 방지로 비활성화됨`}
                                  className="text-xs px-2 py-0.5 bg-gray-100 text-gray-400 border border-gray-200 rounded-full whitespace-nowrap cursor-not-allowed">
                                  {c.label}{c.name ? ` ${c.name}` : ''}
                                  <span className="ml-1 text-gray-400">· 추가됨</span>
                                </span>
                              );
                            }
                            return (
                              <button key={`${c.label}-${c.to}`} type="button"
                                onClick={() => setSmsRecipients([...smsRecipients, c])}
                                className="text-xs px-2 py-0.5 bg-white text-indigo-700 border border-indigo-200 rounded-full hover:bg-indigo-100 cursor-pointer whitespace-nowrap">
                                {c.label}{c.name ? ` ${c.name}` : ''}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">템플릿 불러오기</label>
                  {smsTemplates.length > 0 ? (
                    <div className="flex gap-1.5 flex-wrap">
                      {smsTemplates.map((t: any) => (
                        <button key={t.photoSmsTemplateIdx} type="button" onClick={() => applyTemplate(t)}
                          className="text-xs px-2.5 py-1.5 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 cursor-pointer">
                          {t.templateName}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400">등록된 템플릿이 없습니다. 문자전송 탭 &gt; 템플릿 관리에서 추가할 수 있습니다.</p>
                  )}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-sm font-medium text-gray-700">내용 *</label>
                    <span className="text-xs text-gray-400">클릭해서 변수 삽입</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {SMS_TEMPLATE_VARS.map(v => (
                      <button
                        key={v.key}
                        type="button"
                        onClick={() => insertSmsTextVar(v.key)}
                        className="px-2.5 py-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full hover:bg-blue-100 cursor-pointer whitespace-nowrap"
                      >
                        + {v.label}
                      </button>
                    ))}
                  </div>
                  <textarea
                    ref={smsTextRef}
                    value={smsForm.text}
                    onChange={(e) => setSmsForm({ ...smsForm, text: e.target.value })}
                    rows={5} required
                    placeholder="문자 내용을 입력하세요."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {(() => {
                    const chars = (smsForm.text || '').length;
                    const bytes = countSmsBytes(smsForm.text || '');
                    const isLms = bytes > 90;
                    return (
                      <div className="flex items-center justify-end mt-1">
                        <span className={`text-xs ${isLms ? 'text-yellow-700' : 'text-gray-400'}`}>
                          {chars}자 · {bytes}바이트 · <span className={isLms ? 'font-semibold' : ''}>{isLms ? 'LMS' : 'SMS'}</span>
                          {isLms && <span className="ml-1 text-gray-400">(90바이트 초과)</span>}
                        </span>
                      </div>
                    );
                  })()}
                  <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <div className="text-xs text-gray-500 mb-1.5 flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      <span>미리보기 (실제 전송 시 이렇게 보입니다)</span>
                    </div>
                    <div className="text-sm text-gray-800 whitespace-pre-wrap min-h-[2.5rem] leading-relaxed">
                      {smsForm.text
                        ? renderSmsPreview(smsForm.text, customer)
                        : <span className="text-gray-400">내용을 입력하면 미리보기가 표시됩니다.</span>}
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
                  <button type="button" onClick={closeSmsModal}
                    className="px-5 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-sm text-gray-600 cursor-pointer">
                    취소
                  </button>
                  <button type="submit" disabled={smsRecipients.length === 0}
                    className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium cursor-pointer flex items-center gap-1.5">
                    <i className="ri-send-plane-line"></i>
                    {smsRecipients.length > 1 ? `${smsRecipients.length}명에게 발송` : '발송'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* 문자 템플릿 등록/수정 모달 */}
        {showTemplateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
                <h2 className="text-xl font-bold">{editingTemplate ? '템플릿 수정' : '템플릿 추가'}</h2>
                <button onClick={() => setShowTemplateModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-2xl"></i></button>
              </div>
              <form onSubmit={handleSaveTemplate} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">템플릿명 *</label>
                  <input type="text" value={templateForm.templateName} onChange={(e) => setTemplateForm({ ...templateForm, templateName: e.target.value })}
                    required placeholder="예: 촬영 1주일 전 알림"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">유형</label>
                  <select value={templateForm.templateType} onChange={(e) => setTemplateForm({ ...templateForm, templateType: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm">
                    <option value="GENERAL">일반</option>
                    <option value="SHOOTING_REMINDER">촬영알림</option>
                    <option value="SELECT_REMINDER">셀렉알림</option>
                    <option value="DELIVERY_NOTICE">납품알림</option>
                    <option value="PAYMENT_REMINDER">결제알림</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">제목</label>
                  <input type="text" value={templateForm.title} onChange={(e) => setTemplateForm({ ...templateForm, title: e.target.value })}
                    placeholder="문자 제목 (선택)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-sm font-medium text-gray-700">내용 *</label>
                    <span className="text-xs text-gray-400">클릭해서 변수 삽입</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {SMS_TEMPLATE_VARS.map(v => (
                      <button
                        key={v.key}
                        type="button"
                        onClick={() => insertTemplateVar(v.key)}
                        className="px-2.5 py-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full hover:bg-blue-100 cursor-pointer whitespace-nowrap"
                      >
                        + {v.label}
                      </button>
                    ))}
                  </div>
                  <textarea
                    ref={templateContentRef}
                    value={templateForm.content}
                    onChange={(e) => setTemplateForm({ ...templateForm, content: e.target.value })}
                    rows={6} required
                    placeholder="문자 내용을 입력하세요. 위 변수 버튼을 클릭하면 커서 위치에 삽입됩니다."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                  />
                  {(() => {
                    const chars = (templateForm.content || '').length;
                    const bytes = countSmsBytes(templateForm.content || '');
                    const isLms = bytes > 90;
                    return (
                      <div className="flex items-center justify-end mt-1">
                        <span className={`text-xs ${isLms ? 'text-yellow-700' : 'text-gray-400'}`}>
                          {chars}자 · {bytes}바이트 · <span className={isLms ? 'font-semibold' : ''}>{isLms ? 'LMS' : 'SMS'}</span>
                          {isLms && <span className="ml-1 text-gray-400">(90바이트 초과 시 LMS)</span>}
                        </span>
                      </div>
                    );
                  })()}
                  <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <div className="text-xs text-gray-500 mb-1.5 flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      <span>미리보기 (실제 전송 시 이렇게 보입니다)</span>
                    </div>
                    <div className="text-sm text-gray-800 whitespace-pre-wrap min-h-[2.5rem] leading-relaxed">
                      {templateForm.content
                        ? renderSmsPreview(templateForm.content, customer)
                        : <span className="text-gray-400">내용을 입력하면 미리보기가 표시됩니다.</span>}
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                  <button type="button" onClick={() => setShowTemplateModal(false)}
                    className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium cursor-pointer">취소</button>
                  <button type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium cursor-pointer">{editingTemplate ? '수정' : '등록'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* 발주확인(역발주) 모달 */}
        {showOrderConfirmModal && (() => {
          const formatDt = (dt?: string) => dt ? new Date(dt).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' }) : '-';
          const ctLabel = customer?.customerType === 'REHEARSAL_AND_MAIN' ? '리허설+본식' : customer?.customerType === 'REHEARSAL' ? '리허설' : customer?.customerType === 'MAIN' ? '본식' : '-';
          const latestContract = tabData?.contracts?.[0];
          const contractItems = latestContract?.items || [];
          const formatAmt = (v?: number) => v ? Number(v).toLocaleString() + '원' : '-';
          return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
              <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h2 className="text-lg font-bold text-gray-900">발주확인(역발주)서 발송</h2>
                <button onClick={() => setShowOrderConfirmModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-xl"></i></button>
              </div>
              <div className="grid grid-cols-2 divide-x divide-gray-200">
                {/* 좌측: PDF 미리보기 */}
                <div className="p-5">
                  <p className="text-xs text-gray-400 mb-3 flex items-center gap-1"><i className="ri-eye-line"></i> 미리보기</p>
                  {orderConfirmPdfUrl ? (
                    <iframe src={orderConfirmPdfUrl} className="w-full h-[500px] border border-gray-200 rounded-lg" title="미리보기" />
                  ) : (
                    <div className="flex items-center justify-center h-[500px] text-gray-400">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-2"></div>
                      PDF 생성중...
                    </div>
                  )}
                </div>
                {/* 우측: 발송 정보 입력 */}
                <form onSubmit={handleSendOrderConfirmation} className="p-5 space-y-4">
                  <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-700">
                    <i className="ri-information-line mr-1"></i>
                    좌측 내용이 이메일로 발송됩니다.
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">수신 이메일 *</label>
                    <input type="email" value={orderConfirmForm.recipientEmail} onChange={(e) => setOrderConfirmForm({ ...orderConfirmForm, recipientEmail: e.target.value })}
                      required placeholder="planner@example.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">수신자명 (플래너/업체명)</label>
                    <input type="text" value={orderConfirmForm.recipientName} onChange={(e) => setOrderConfirmForm({ ...orderConfirmForm, recipientName: e.target.value })}
                      placeholder="예: 아이웨딩 김플래너"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
                    <button type="button" onClick={() => setShowOrderConfirmModal(false)}
                      className="px-5 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium cursor-pointer text-sm">취소</button>
                    <button type="submit"
                      className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium cursor-pointer text-sm flex items-center gap-1.5">
                      <i className="ri-mail-send-line"></i> 발송
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          );
        })()}

        {/* 계약 등록 모달 */}
        {showContractModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
                <h2 className="text-xl font-bold">계약 등록</h2>
                <button onClick={() => setShowContractModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-2xl"></i></button>
              </div>
              <form onSubmit={handleCreateContract} className="p-6 space-y-6">
                {/* 고객 선택 */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <i className="ri-user-line text-gray-400"></i> 고객 선택
                  </h3>
                  {contractSelectedCustomer ? (
                    <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-sm font-medium text-blue-600">
                        {contractSelectedCustomer.name?.charAt(0)}
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{contractSelectedCustomer.name}</div>
                        <div className="text-xs text-gray-500">{contractSelectedCustomer.phone}{contractSelectedCustomer.weddingDt ? ` | 예식: ${new Date(contractSelectedCustomer.weddingDt).toLocaleDateString('ko-KR')}` : ''}</div>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${CUSTOMER_STATUS_COLORS[contractSelectedCustomer.status] || 'bg-gray-100 text-gray-700'}`}>{contractSelectedCustomer.status}</span>
                      <button type="button" onClick={() => { setContractSelectedCustomer(null); setContractCustomerQuery(''); setContractCustomerStatusFilter(''); }}
                        className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-lg"></i></button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* 상태 필터 */}
                      <div className="flex flex-wrap gap-1.5">
                        <button type="button" onClick={() => setContractCustomerStatusFilter('')}
                          className={`px-2.5 py-1 rounded-md text-xs transition-colors cursor-pointer ${
                            contractCustomerStatusFilter === '' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}>전체</button>
                        {CUSTOMER_STATUSES.map(s => (
                          <button key={s} type="button" onClick={() => setContractCustomerStatusFilter(contractCustomerStatusFilter === s ? '' : s)}
                            className={`px-2.5 py-1 rounded-md text-xs transition-colors cursor-pointer ${
                              contractCustomerStatusFilter === s ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}>{s}</button>
                        ))}
                      </div>
                      {/* 검색 */}
                      <div className="relative">
                        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                        <input type="text" placeholder="고객명 또는 전화번호로 검색" value={contractCustomerQuery}
                          onChange={(e) => { setContractCustomerQuery(e.target.value); setShowCustomerDropdown(true); }}
                          onFocus={() => setShowCustomerDropdown(true)}
                          className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                      {/* 검색 결과 목록 */}
                      {showCustomerDropdown && contractCustomerResults.length > 0 && (
                        <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                          {contractCustomerResults.map((c: any) => (
                            <button key={c.photoCustomerIdx} type="button"
                              onClick={() => selectContractCustomer(c)}
                              className="w-full px-4 py-2.5 text-left hover:bg-blue-50 flex items-center gap-3 cursor-pointer border-b border-gray-100 last:border-b-0">
                              <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-600">
                                {c.name?.charAt(0)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900">{c.name}</div>
                                <div className="text-xs text-gray-500">{c.phone}{c.weddingDt ? ` | 예식: ${new Date(c.weddingDt).toLocaleDateString('ko-KR')}` : ''}</div>
                              </div>
                              <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${CUSTOMER_STATUS_COLORS[c.status] || 'bg-gray-100 text-gray-700'}`}>{c.status}</span>
                            </button>
                          ))}
                        </div>
                      )}
                      {showCustomerDropdown && (contractCustomerQuery || contractCustomerStatusFilter) && contractCustomerResults.length === 0 && (
                        <div className="text-center py-4 text-sm text-gray-400">검색 결과가 없습니다.</div>
                      )}
                    </div>
                  )}
                </div>

                {/* 계약 기본 정보 */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <i className="ri-file-text-line text-gray-400"></i> 계약 정보
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">계약 유형 *</label>
                      <select value={contractForm.contractType || 'REHEARSAL_AND_MAIN'} onChange={(e) => setContractForm({ ...contractForm, contractType: e.target.value })} required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                        {CONTRACT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">계약일 *</label>
                      <input type="date" value={contractForm.contractDt || ''} onChange={(e) => setContractForm({ ...contractForm, contractDt: e.target.value })} required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">메모</label>
                    <textarea value={contractForm.memo || ''} onChange={(e) => setContractForm({ ...contractForm, memo: e.target.value })} rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                  </div>
                </div>

                {/* 품목 */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                      <i className="ri-list-check text-gray-400"></i> 품목 목록
                    </h3>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => openProductSelector(-1)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1 cursor-pointer">
                        <i className="ri-search-line"></i> 상품에서 추가
                      </button>
                      <button type="button" onClick={addContractItemManual}
                        className="text-gray-600 hover:text-gray-800 text-sm font-medium flex items-center gap-1 cursor-pointer">
                        <i className="ri-add-line"></i> 직접 입력
                      </button>
                    </div>
                  </div>

                  {contractItems.length === 0 ? (
                    <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center">
                      <i className="ri-shopping-bag-line text-3xl text-gray-300 block mb-2"></i>
                      <p className="text-sm text-gray-400 mb-3">품목을 추가해주세요</p>
                      <div className="flex justify-center gap-3">
                        <button type="button" onClick={() => openProductSelector(-1)}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 cursor-pointer flex items-center gap-1">
                          <i className="ri-search-line"></i> 상품 검색
                        </button>
                        <button type="button" onClick={addContractItemManual}
                          className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 cursor-pointer flex items-center gap-1">
                          <i className="ri-edit-line"></i> 직접 입력
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {contractItems.map((item, idx) => (
                        <div key={item.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
                          <div className="flex justify-between items-center mb-3">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-semibold text-gray-500">품목 {idx + 1}</span>
                              {item.productIdx && <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded">상품연동</span>}
                            </div>
                            <div className="flex gap-2">
                              <button type="button" onClick={() => openProductSelector(idx)}
                                className="text-blue-500 hover:text-blue-700 text-xs cursor-pointer">상품변경</button>
                              <button type="button" onClick={() => removeContractItem(item.id)}
                                className="text-red-500 hover:text-red-700 text-xs cursor-pointer">삭제</button>
                            </div>
                          </div>
                          <div className="grid grid-cols-4 gap-3">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">카테고리</label>
                              <select value={item.itemCategory} onChange={(e) => updateContractItem(item.id, 'itemCategory', e.target.value)}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                                {ITEM_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">상품명 *</label>
                              <input type="text" value={item.productName} onChange={(e) => updateContractItem(item.id, 'productName', e.target.value)}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">수량</label>
                              <input type="number" min={1} value={item.quantity} onChange={(e) => updateContractItem(item.id, 'quantity', Number(e.target.value))}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">단가 *</label>
                              <input type="number" value={item.unitPrice} onChange={(e) => updateContractItem(item.id, 'unitPrice', Number(e.target.value))}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                            </div>
                            <div className="col-span-2">
                              <label className="block text-xs text-gray-600 mb-1">비고</label>
                              <input type="text" value={item.memo || ''} onChange={(e) => updateContractItem(item.id, 'memo', e.target.value)}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* 합계 */}
                  {contractItems.length > 0 && (
                    <div className="mt-4 bg-blue-50 rounded-lg p-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">총 금액</span>
                        <span className="font-semibold text-gray-900">{contractItems.reduce((sum: number, item: any) => sum + (item.unitPrice * item.quantity), 0).toLocaleString()}원</span>
                      </div>
                      <div className="flex justify-between text-sm mt-1 pt-1 border-t border-blue-200">
                        <span className="font-semibold text-gray-800">최종 금액</span>
                        <span className="font-bold text-blue-700">
                          {contractItems.reduce((sum: number, item: any) => sum + (item.unitPrice * item.quantity), 0).toLocaleString()}원
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button type="button" onClick={() => setShowContractModal(false)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">취소</button>
                  <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer">등록</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* 상품 선택 모달 */}
        {showProductModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-lg font-bold">상품 선택</h2>
                <button onClick={() => { setShowProductModal(false); setAddingItemIndex(null); }} className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-2xl"></i></button>
              </div>
              <div className="px-6 py-3 border-b border-gray-100">
                <div className="relative">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                  <input type="text" placeholder="상품명 또는 상품코드로 검색" value={productQuery}
                    onChange={(e) => setProductQuery(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" autoFocus />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto">
                {products.length === 0 ? (
                  <div className="p-12 text-center text-gray-400">
                    <i className="ri-shopping-bag-line text-3xl block mb-2"></i>
                    {productQuery ? '검색 결과가 없습니다.' : '등록된 상품이 없습니다.'}
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {products.map((product: any) => (
                      <button key={product.productIdx} type="button"
                        onClick={() => selectProduct(product)}
                        className="w-full px-6 py-3 text-left hover:bg-blue-50 flex items-center gap-4 cursor-pointer transition-colors">
                        <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          {product.thumbnailUrl ? (
                            <img src={product.thumbnailUrl} alt="" className="w-10 h-10 rounded-lg object-cover" />
                          ) : (
                            <i className="ri-camera-lens-line text-gray-400"></i>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900 truncate">{product.name}</span>
                            {product.productCode && <span className="text-[10px] text-gray-400">{product.productCode}</span>}
                          </div>
                          <div className="text-xs text-gray-500">
                            {product.category && <span className="mr-2">{ITEM_CATEGORIES.find(c => c.value === product.category)?.label || product.category}</span>}
                            {product.status && <span className={`px-1.5 py-0.5 rounded ${product.status === 'SALE' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'}`}>{product.status === 'SALE' ? '판매중' : product.status}</span>}
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-sm font-semibold text-gray-900">{(product.salesPrice || 0).toLocaleString()}원</div>
                          {product.costPrice > 0 && <div className="text-[10px] text-gray-400">공급가 {product.costPrice.toLocaleString()}원</div>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 결제 상세 모달 */}
        {selectedPayment && (() => {
          const p = selectedPayment;
          const paymentType = p.paymentType || p.payment_type || '';
          const paymentMethod = p.paymentMethod || p.payment_method || '';
          const paidDt = p.paidDt || p.paid_dt || '';
          const status = p.status || '';
          const paymentIdx = p.photoPaymentIdx || p.photo_payment_idx;
          const getTypeLabel = (t: string) => ({ FULL: '일시납부', CONTRACT: '계약금', INTERMEDIATE: '중도금', BALANCE: '잔금', ETC: '기타' }[t] || t);
          const getMethodLabel = (m: string) => ({ CARD: '카드', TRANSFER: '계좌이체', CASH: '현금' }[m] || m || '-');
          const getStatusLabel = (s: string) => ({ COMPLETED: '완료', SCHEDULED: '예정', REFUNDED: '환불', CANCELLED: '취소' }[s] || s);
          const fmtDt = (dt: string) => { if (!dt) return '-'; try { return new Date(dt).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' }); } catch { return dt; } };
          return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                  <h2 className="text-lg font-bold">결제 상세</h2>
                  <button onClick={() => setSelectedPayment(null)} className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-2xl"></i></button>
                </div>
                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div><div className="text-xs text-gray-500 mb-1">계약</div><div className="text-sm font-medium">{(() => { const c = (tabData?.contracts || []).find((c: any) => (c.photoContractIdx || c.photo_contract_idx) === (p.photoContractIdx || p.photo_contract_idx)); return c?.contractNo || c?.contract_no || '-'; })()}</div></div>
                    <div><div className="text-xs text-gray-500 mb-1">결제일</div><div className="text-sm font-medium">{fmtDt(paidDt)}</div></div>
                    <div><div className="text-xs text-gray-500 mb-1">결제유형</div><div><span className={`text-xs px-2 py-1 rounded-full ${PAYMENT_TYPE_COLORS[paymentType] || 'bg-gray-100'}`}>{getTypeLabel(paymentType)}</span></div></div>
                    <div><div className="text-xs text-gray-500 mb-1">금액</div><div className="text-lg font-bold text-gray-900">{Number(p.amount || 0).toLocaleString()}원</div></div>
                    <div><div className="text-xs text-gray-500 mb-1">결제수단</div><div className="text-sm">{getMethodLabel(paymentMethod)}</div></div>
                    <div><div className="text-xs text-gray-500 mb-1">상태</div><div><span className={`text-xs px-2 py-1 rounded-full ${PAYMENT_STATUS_COLORS[status] || 'bg-gray-100'}`}>{getStatusLabel(status)}</span></div></div>
                  </div>
                  {p.memo && <div><div className="text-xs text-gray-500 mb-1">메모</div><div className="text-sm text-gray-700">{p.memo}</div></div>}
                </div>
                <div className="px-6 py-4 border-t border-gray-100 flex justify-between">
                  <button onClick={async () => {
                    if (!confirm('이 결제를 삭제하시겠습니까?')) return;
                    try {
                      await paymentApi.delete(paymentIdx);
                      setSelectedPayment(null);
                      fetchData();
                    } catch (err: any) { alert(`삭제 실패: ${err.message}`); }
                  }} className="text-red-500 hover:text-red-700 text-sm cursor-pointer flex items-center gap-1">
                    <i className="ri-delete-bin-line"></i> 삭제
                  </button>
                  <button onClick={() => setSelectedPayment(null)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 cursor-pointer">닫기</button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* 결제 등록 모달 (여러 건 동시 등록) */}
        {showPaymentModal && (() => {
          const paymentItems = paymentForm.items || [{ paidDt: new Date().toISOString().split('T')[0], amount: 0, paymentType: 'CONTRACT', status: 'COMPLETED', paymentMethod: 'CARD', memo: '' }];
          const updatePaymentItem = (idx: number, field: string, value: any) => {
            const newItems = [...paymentItems];
            newItems[idx] = { ...newItems[idx], [field]: value };
            setPaymentForm({ ...paymentForm, items: newItems });
          };
          const addPaymentItem = () => {
            setPaymentForm({ ...paymentForm, items: [...paymentItems, { paidDt: new Date().toISOString().split('T')[0], amount: 0, paymentType: 'INTERMEDIATE', status: 'SCHEDULED', paymentMethod: 'CARD', memo: '' }] });
          };
          const removePaymentItem = (idx: number) => {
            if (paymentItems.length <= 1) return;
            setPaymentForm({ ...paymentForm, items: paymentItems.filter((_: any, i: number) => i !== idx) });
          };

          return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
                <div>
                  <h2 className="text-lg font-bold">결제 등록</h2>
                  <p className="text-xs text-gray-400 mt-0.5">여러 결제를 한번에 등록할 수 있습니다</p>
                </div>
                <button onClick={() => setShowPaymentModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-2xl"></i></button>
              </div>
              <form onSubmit={handleCreatePayment} className="p-6 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">계약 선택 *</label>
                  <select value={paymentForm.photoContractIdx || ''} onChange={(e) => setPaymentForm({ ...paymentForm, photoContractIdx: e.target.value })} required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">계약을 선택하세요</option>
                    {(tabData?.contracts || []).map((c: any) => {
                      const idx = c.photoContractIdx || c.photo_contract_idx;
                      const no = c.contractNo || c.contract_no || `계약 ${idx}`;
                      const amt = c.finalAmount || c.final_amount || 0;
                      const citems = contractItemsMap[idx] || [];
                      const itemNames = citems.map((i: any) => i.productName || i.product_name).filter(Boolean).join(', ');
                      return <option key={idx} value={idx}>{no} - {itemNames || '품목없음'} ({Number(amt).toLocaleString()}원)</option>;
                    })}
                  </select>
                </div>

                {paymentItems.map((pi: any, idx: number) => (
                  <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-xs font-semibold text-gray-500">결제 {idx + 1}</span>
                      {paymentItems.length > 1 && (
                        <button type="button" onClick={() => removePaymentItem(idx)} className="text-red-500 hover:text-red-700 text-xs cursor-pointer">삭제</button>
                      )}
                    </div>
                    <div className="grid grid-cols-4 gap-3">
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">결제일 *</label>
                        <input type="date" value={pi.paidDt || ''} onChange={(e) => updatePaymentItem(idx, 'paidDt', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">금액 *</label>
                        <input type="number" value={pi.amount || ''} onChange={(e) => updatePaymentItem(idx, 'amount', Number(e.target.value))}
                          placeholder="0" className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">결제유형</label>
                        <select value={pi.paymentType || 'CONTRACT'} onChange={(e) => updatePaymentItem(idx, 'paymentType', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                          {PAYMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">상태</label>
                        <select value={pi.status || 'COMPLETED'} onChange={(e) => updatePaymentItem(idx, 'status', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                          {PAYMENT_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">결제수단</label>
                        <select value={pi.paymentMethod || 'CARD'} onChange={(e) => updatePaymentItem(idx, 'paymentMethod', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                          {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">메모</label>
                        <input type="text" value={pi.memo || ''} onChange={(e) => updatePaymentItem(idx, 'memo', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">결제 출처</label>
                        <select value={pi.paymentSource || 'DIRECT'} onChange={(e) => { updatePaymentItem(idx, 'paymentSource', e.target.value); if (e.target.value === 'DIRECT') updatePaymentItem(idx, 'paymentSourceName', ''); }}
                          className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                          <option value="DIRECT">고객 직접</option>
                          <option value="VENDOR">업체 정산</option>
                        </select>
                      </div>
                      {pi.paymentSource === 'VENDOR' && (
                        <div>
                          <label className="block text-[10px] text-gray-500 mb-0.5">정산 업체명</label>
                          <input type="text" value={pi.paymentSourceName || ''} onChange={(e) => updatePaymentItem(idx, 'paymentSourceName', e.target.value)}
                            placeholder="예: 아이웨딩 플래너"
                            className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                <button type="button" onClick={addPaymentItem}
                  className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-blue-400 hover:text-blue-600 cursor-pointer transition-colors flex items-center justify-center gap-1">
                  <i className="ri-add-line"></i> 결제 추가
                </button>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button type="button" onClick={() => setShowPaymentModal(false)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">취소</button>
                  <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer">등록</button>
                </div>
              </form>
            </div>
          </div>
          );
        })()}
      </div>
    </div>
  );
}
